import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import api, { jobDescriptionAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import PageHeader from '../components/ui/PageHeader';
import JobDescriptionPendingList from '../components/JobDescriptionPendingList';
import Breadcrumbs from '../components/ui/Breadcrumbs';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Badge from '../components/ui/Badge';
import Spinner from '../components/ui/Spinner';
import EmptyState from '../components/ui/EmptyState';
import Modal from '../components/ui/Modal';
import SignPasswordModal from '../components/SignPasswordModal';
import { useToast } from '../context/ToastContext';

const EmployeeSheets = ({ scopedCompanyId }) => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const embedded = Boolean(scopedCompanyId);
  const companyIdFilter = scopedCompanyId || searchParams.get('companyId');

  const [sheets, setSheets] = useState([]);
  const [pendingTests, setPendingTests] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(companyIdFilter || '');
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [selectedJobTitle, setSelectedJobTitle] = useState('');
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [onlyMyPending, setOnlyMyPending] = useState(searchParams.get('pending') === 'true');
  const [downloading, setDownloading] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [expandedEmployee, setExpandedEmployee] = useState(null);

  const isSSMRole = ['SUPER_ADMIN', 'SSM_COMPANY_ADMIN', 'SSM_INSTRUCTOR'].includes(user?.role);

  const [myManagedDeptIds, setMyManagedDeptIds] = useState([]);
  const [, setMyManagedDeptNames] = useState([]);
  const [companyDepartments, setCompanyDepartments] = useState([]);
  const [admitConfirm, setAdmitConfirm] = useState(null); // { employee, domain }

  // Card-ul de teste e collapsable — implicit expandat dacă există teste pendente.
  // Toggle prin click pe header (similar cu rândul angajat pe lista de fișe).
  // Restrâns implicit — când sunt multe teste, randarea listei încărca mult pagina. Userul îl
  // deschide din header sau din stat-ul „Teste de semnat" (showAllPendingTests îl expandează).
  const [testsCardExpanded, setTestsCardExpanded] = useState(false);

  // Modal preview test — afișează întrebările + răspunsurile date de angajat, cu cod color
  // (verde = corect, roșu = greșit), similar cu secțiunea de pe SignaturePage.
  // State: null = închis; { trainingId, loading } sau { trainingId, training } când datele sosesc.
  const [viewTest, setViewTest] = useState(null);
  const openTestView = async (trainingId, courseTitle, employeeName) => {
    setViewTest({ trainingId, loading: true, courseTitle, employeeName });
    try {
      const res = await api.get(`/trainings/${trainingId}`);
      setViewTest({ trainingId, loading: false, training: res.data, courseTitle, employeeName });
    } catch (err) {
      showToast(t('sheets.loadTestError'), 'error');
      setViewTest(null);
    }
  };

  // Bulk signing
  const [bulkMode, setBulkMode] = useState(false);
  const [selectedTrainingIds, setSelectedTrainingIds] = useState(new Set());
  // La bulk sign, testele finale (semnate cu /signatures/test, nu /signatures/bulk) sunt ținute
  // separat în propria selecție — endpoint-ul de bulk ia doar fișe, deci la submit iterăm și
  // testele cu același password introdus o singură dată.
  const [selectedTestTrainingIds, setSelectedTestTrainingIds] = useState(new Set());
  // Admis-la-lucru — selectabil explicit în bulk (ca user-ul să vadă ce ANUME va fi admis).
  // Cheia e `${employeeId}-${domain}`. Anterior, admis-la-lucru rula automat pe toți angajații
  // afectați (silent) și user-ul nu vedea ce s-a întâmplat. Acum: checkbox vizibil per (angajat, domeniu)
  // în zona expandată a angajatului, pre-bifat dacă admis e eligibil + neblocat.
  const [selectedAdmissionItems, setSelectedAdmissionItems] = useState(new Set());
  // Fișa postului — pending pentru admin/SSM curent + selecția pentru bulk-sign integrat.
  const [jdPending, setJdPending] = useState([]);
  const [jdSelectedIds, setJdSelectedIds] = useState(new Set());
  const [showBulkConfirm, setShowBulkConfirm] = useState(false);
  // Popup cu lista completă a tot ce se semnează în bulk (fișe + teste + fișa postului + admiteri).
  // Deschis dintr-un link în modalul de confirmare când lista e prea lungă pentru a fi afișată inline.
  const [showBulkFullList, setShowBulkFullList] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [showBulkPwdModal, setShowBulkPwdModal] = useState(false);
  const [pendingTestSignTrainingId, setPendingTestSignTrainingId] = useState(null);
  const [pendingAdmit, setPendingAdmit] = useState(null); // { employeeId, domain }
  const [bulkAdmitOpen, setBulkAdmitOpen] = useState(false); // arată modal cu lista de eligibili
  // Setul de chei selectate (`${employeeId}-${domain}`) din lista de admiteri.
  // La deschiderea modalului se setează automat cu TOATE (default „totul bifat").
  const [selectedAdmitKeys, setSelectedAdmitKeys] = useState(new Set());
  const [pendingBulkAdmitItems, setPendingBulkAdmitItems] = useState(null);
  const [bulkAdmitResult, setBulkAdmitResult] = useState(null); // { admitted, skipped, details }
  // Popup explicativ când userul dă click pe o admitere blocată sau pe „Admite" la un angajat
  // care încă nu are toate semnăturile. Conține { employeeName, domain, blockers[] }.
  const [blockedAdmissionInfo, setBlockedAdmissionInfo] = useState(null);

  useEffect(() => {
    fetchCompanies();
    fetchPendingTests();
    fetchJobDescriptionsPending();
  }, []);

  const fetchJobDescriptionsPending = async () => {
    try {
      const { data } = await jobDescriptionAPI.getPendingForMe();
      setJdPending(data.pending || []);
    } catch (_) {
      /* silent — feature opțional */
    }
  };

  const toggleJdSelection = (instanceId) => {
    setJdSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(instanceId)) next.delete(instanceId);
      else next.add(instanceId);
      return next;
    });
  };

  const fetchPendingTests = async () => {
    try {
      const res = await api.get('/signatures/test-pending');
      setPendingTests(res.data || []);
    } catch {
      setPendingTests([]);
    }
  };

  const handleSignTest = (trainingId) => {
    setPendingTestSignTrainingId(trainingId);
  };

  const submitAdmit = async (password) => {
    try {
      await api.post('/signatures/admit', {
        employeeId: pendingAdmit.employeeId,
        domain: pendingAdmit.domain,
        password,
      });
      setPendingAdmit(null);
      await fetchSheets();
    } catch (err) {
      const data = err.response?.data;
      if (data?.requiresAdESActivation) {
        setPendingAdmit(null);
        showToast(data.error || t('sheets.admitError'), 'error');
        return;
      }
      throw err;
    }
  };

  const submitBulkAdmit = async (password) => {
    if (!pendingBulkAdmitItems || pendingBulkAdmitItems.length === 0) return;
    try {
      const res = await api.post('/signatures/admit-bulk', {
        items: pendingBulkAdmitItems,
        password,
      });
      setPendingBulkAdmitItems(null);
      setBulkAdmitOpen(false);
      setBulkAdmitResult(res.data);
      const toastType = res.data.admitted > 0 ? 'success' : 'warning';
      showToast(res.data.message || t('sheets.bulkAdmitSuccess'), toastType);
      await fetchSheets();
    } catch (err) {
      const data = err.response?.data;
      if (data?.requiresAdESActivation) {
        setPendingBulkAdmitItems(null);
        showToast(data.error || t('sheets.admitError'), 'error');
        return;
      }
      throw err;
    }
  };

  const submitSignTest = async (password) => {
    try {
      await api.post('/signatures/test', { trainingId: pendingTestSignTrainingId, password });
      setPendingTestSignTrainingId(null);
      fetchPendingTests();
    } catch (err) {
      const data = err.response?.data;
      if (data?.requiresAdESActivation) {
        setPendingTestSignTrainingId(null);
        showToast(data.error || t('sheets.signError'), 'error');
        return;
      }
      throw err;
    }
  };
  useEffect(() => {
    if (user) fetchMyDepartments();
  }, [user]);

  // Plain EMPLOYEE (fără manager dept și fără flag PSI/SSMInst) NU are ce căuta aici.
  // EMPLOYEE cu flag isCadruTehnicPSI sau isSSMInstructor TREBUIE să poată semna fișe SU/SSM
  // (sunt instructori interni / cadru PSI), deci nu-i mai redirecționăm.
  useEffect(() => {
    const employeeHasSigningRole =
      user?.isCadruTehnicPSI === true ||
      user?.isSSMInstructor === true ||
      user?.isCLM === true ||
      myManagedDeptIds.length > 0;
    if (user?.role === 'EMPLOYEE' && !employeeHasSigningRole) {
      const t = setTimeout(() => {
        const stillNoRole =
          user?.role === 'EMPLOYEE' &&
          user?.isCadruTehnicPSI !== true &&
          user?.isSSMInstructor !== true &&
          user?.isCLM !== true &&
          myManagedDeptIds.length === 0;
        if (stillNoRole) {
          navigate('/my-certificates', { replace: true });
        }
      }, 400);
      return () => clearTimeout(t);
    }
  }, [user, myManagedDeptIds, navigate]);
  useEffect(() => {
    fetchSheets();
  }, [selectedCompany]);
  useEffect(() => {
    // Încarcă toate departamentele firmei selectate (sau ale firmei userului dacă nu e selecție)
    const targetCompanyId = selectedCompany || user?.companyId;
    if (targetCompanyId) {
      api
        .get(`/departments?companyId=${targetCompanyId}`)
        .then((res) => setCompanyDepartments((res.data || []).map((d) => d.name)))
        .catch(() => setCompanyDepartments([]));
    } else {
      setCompanyDepartments([]);
    }
  }, [selectedCompany, user]);

  const fetchCompanies = async () => {
    try {
      const res = await api.get('/companies');
      setCompanies(res.data || []);
    } catch (e) {}
  };

  const fetchMyDepartments = async () => {
    try {
      if (!user?.companyId) return;
      const res = await api.get(`/departments?companyId=${user.companyId}`);
      const depts = res.data || [];
      // Departamentele unde eu sunt manager
      const mine = depts.filter((d) => d.managerId === user?.id);
      setMyManagedDeptIds(mine.map((d) => d.id));
      setMyManagedDeptNames(mine.map((d) => d.name));
    } catch (e) {}
  };

  const fetchSheets = async () => {
    setLoading(true);
    try {
      let url = '/trainings?status=PASSED&showDeleted=true';
      if (selectedCompany) url += `&companyId=${selectedCompany}`;
      const res = await api.get(url);
      const withSigs = (res.data || []).filter((t) => t.certificate || t.signatures?.length > 0);
      setSheets(withSigs);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Signer slot types — matches backend signatureResolver MATRIX (single source of truth)
  // Returns null when course is not applicable for that trainee role.
  const getExpectedSignerTypes = (
    courseType,
    traineeRole,
    isTraineeCLM,
    _hasDirectorGeneral = false
  ) => {
    const MATRIX = {
      EMPLOYEE: {
        IIG_SSM: ['EMPLOYEE', 'SSM', 'SSM'],
        ILM_SSM: ['EMPLOYEE', 'MANAGER', 'ADMIN'],
        IP_SSM: ['EMPLOYEE', 'MANAGER', 'ADMIN'],
        SUPLIMENTARE_SSM: ['EMPLOYEE', 'MANAGER', 'ADMIN'],
        IIG_SU: ['EMPLOYEE', 'CADRU_PSI'],
        ILM_SU: ['EMPLOYEE', 'MANAGER'],
        IP_SU: ['EMPLOYEE', 'MANAGER'],
        SUPLIMENTARE_SU: null,
      },
      DIRECTOR_GENERAL: {
        IIG_SSM: ['EMPLOYEE', 'SSM', 'SSM'],
        ILM_SSM: ['EMPLOYEE', 'ADMIN', 'ADMIN'],
        IP_SSM: ['EMPLOYEE', 'ADMIN', 'ADMIN'],
        SUPLIMENTARE_SSM: ['EMPLOYEE', 'ADMIN', 'ADMIN'],
        IIG_SU: ['EMPLOYEE', 'CADRU_PSI'],
        ILM_SU: ['EMPLOYEE', 'ADMIN'],
        IP_SU: ['EMPLOYEE', 'ADMIN'],
        SUPLIMENTARE_SU: null,
      },
      // CLM ca persoană instruită: nu se poate semna pe sine la slotul de conducător → ADMINUL
      // preia verificarea (MEREU adminul, poate fi și un împuternicit AdminMandate). Identică cu
      // matricea DIRECTOR_GENERAL. (Identic cu backend `CLM`.)
      CLM: {
        IIG_SSM: ['EMPLOYEE', 'SSM', 'SSM'],
        ILM_SSM: ['EMPLOYEE', 'ADMIN', 'ADMIN'],
        IP_SSM: ['EMPLOYEE', 'ADMIN', 'ADMIN'],
        SUPLIMENTARE_SSM: ['EMPLOYEE', 'ADMIN', 'ADMIN'],
        IIG_SU: ['EMPLOYEE', 'CADRU_PSI'],
        ILM_SU: ['EMPLOYEE', 'ADMIN'],
        IP_SU: ['EMPLOYEE', 'ADMIN'],
        SUPLIMENTARE_SU: null,
      },
      COMPANY_ADMIN: {
        IIG_SSM: ['EMPLOYEE', 'SSM', 'SSM'],
        ILM_SSM: null,
        IP_SSM: ['EMPLOYEE', 'SSM', 'SSM'],
        SUPLIMENTARE_SSM: ['EMPLOYEE', 'SSM', 'SSM'],
        IIG_SU: ['EMPLOYEE', 'CADRU_PSI'],
        ILM_SU: null,
        // SU = domeniul PSI → verificator Cadru Tehnic PSI, nu SSM (analog IIG_SU).
        IP_SU: ['EMPLOYEE', 'CADRU_PSI'],
        SUPLIMENTARE_SU: null,
      },
    };
    // Cursant ANGAJAT: dacă e el însuși CLM (pe propria fișă) → matricea CLM (adminul preia
    // verificarea); altfel matricea EMPLOYEE (slotul MANAGER îl semnează CLM-ul lui).
    // DG/COMPANY_ADMIN cursant → matricile lor.
    let key;
    if (['DIRECTOR_GENERAL', 'COMPANY_ADMIN'].includes(traineeRole)) {
      key = traineeRole;
    } else {
      key = isTraineeCLM ? 'CLM' : 'EMPLOYEE';
    }
    return MATRIX[key]?.[courseType] ?? ['EMPLOYEE'];
  };

  // Detectează dacă traineeu-l e CLM — backend-ul setează flag-ul `_isTraineeCLM` pentru
  // orice angajat care e managerId pe orice departament din firma sa (fallback: dept propriu).
  const isTraineeCLM = (training) => {
    if (training?._isTraineeCLM) return true;
    const traineeUserId = training?.employee?.userId;
    return !!(traineeUserId && training?.employee?.department?.managerId === traineeUserId);
  };

  // Verifică dacă un slot abstract este deja semnat (folosit pentru sloturi reutilizabile:
  // SSM, CADRU_PSI, ADMIN — o singură semnătură umple toate slot-urile de același tip)
  const slotIsFilled = (slotType, sigs, traineeUserId) => {
    return sigs.some((s) => {
      if (slotType === 'EMPLOYEE')
        return s.signerRole === 'EMPLOYEE' && s.signerId === traineeUserId;
      if (slotType === 'SSM')
        return (
          ['SSM_INSTRUCTOR', 'SSM_COMPANY_ADMIN'].includes(s.signerRole) ||
          s.signer?.isSSMInstructor
        );
      if (slotType === 'CADRU_PSI') return s.signer?.isCadruTehnicPSI;
      if (slotType === 'ADMIN') return s.signerRole === 'COMPANY_ADMIN';
      if (slotType === 'DIRECTOR') return s.signerRole === 'DIRECTOR_GENERAL';
      if (slotType === 'MANAGER')
        return (
          s.signerRole === 'MANAGER' ||
          (s.signerRole === 'EMPLOYEE' && s.signerId !== traineeUserId)
        );
      return false;
    });
  };

  // Calculează primul slot ne-semnat (în ordinea matricei).
  // Toate slot-urile non-EMPLOYEE sunt reutilizabile: o singură semnătură umple toate
  // slot-urile de același tip (un admin care semnează o singură dată acoperă ambele
  // slot-uri ADMIN ale CLM-ului, similar cu SSM care apare de două ori).
  const getNextRequiredSlot = (training) => {
    const expected = getExpectedSignerTypes(
      training.course?.type,
      training.employee?.user?.role,
      isTraineeCLM(training),
      !!training._companyHasDG
    );
    const sigs = training.signatures || [];
    const traineeUserId = training.employee?.userId;
    const seenNonEmployee = new Set();
    for (const slot of expected) {
      if (slot === 'EMPLOYEE') {
        if (!sigs.some((s) => s.signerRole === 'EMPLOYEE' && s.signerId === traineeUserId))
          return slot;
        continue;
      }
      if (seenNonEmployee.has(slot)) continue;
      seenNonEmployee.add(slot);
      if (!slotIsFilled(slot, sigs, traineeUserId)) return slot;
    }
    return null;
  };

  // Verifică dacă userul curent poate semna slotul respectiv
  const canFillSlot = (slotType, training) => {
    const role = user?.role;
    const assignedBy = training.assignment?.assignedBy;
    const isOwn = training.employee?.userId === user?.id;
    if (slotType === 'EMPLOYEE') return isOwn;
    if (isOwn) return false; // alte sloturi nu pot fi semnate de angajatul însuși
    if (slotType === 'SSM') {
      if (assignedBy !== user?.id) return false;
      return ['SSM_INSTRUCTOR', 'SSM_COMPANY_ADMIN'].includes(role) || user?.isSSMInstructor;
    }
    if (slotType === 'CADRU_PSI') {
      if (assignedBy !== user?.id) return false;
      return user?.isCadruTehnicPSI;
    }
    if (slotType === 'ADMIN') {
      // COMPANY_ADMIN real al firmei angajatului SAU persoană împuternicită (AdminMandate activ —
      // flag `canSignAsAdmin` setat de backend pe training). Fără flag, împuternicitul nu vedea fișa.
      if (role === 'COMPANY_ADMIN' && training.employee?.companyId === user?.companyId) return true;
      return !!training.canSignAsAdmin;
    }
    if (slotType === 'DIRECTOR') {
      return role === 'DIRECTOR_GENERAL' && training.employee?.companyId === user?.companyId;
    }
    if (slotType === 'MANAGER') {
      const traineeDeptId = training.employee?.departmentId;
      if (traineeDeptId && myManagedDeptIds.includes(traineeDeptId)) return true;
      // Fallback: User.isCLM global (firme fără departamente sau departamente fără manager).
      // Aliniat cu backend `canSignAsManager`. Bug anterior: admin bifat ca CLM global nu
      // vedea slot-ul MANAGER pe nicio fișă în „Fișe de semnat de mine".
      const traineeCompanyId = training.employee?.companyId;
      if (user?.isCLM && traineeCompanyId === user?.companyId) {
        // Doar dacă departamentul trainee-ului (dacă există) NU are alt manager dedicat.
        // Acoperă scenariul firmelor mici fără ierarhie pe departamente.
        if (!traineeDeptId) return true;
        // Trainee are dept dar admin nu îl manageriază — global-CLM nu preia decât
        // dacă dept-ul nu are manager. Aici nu avem managerId în obiectul training;
        // pe siguranță acceptăm și lăsăm backend-ul să blocheze dacă există manager dedicat.
        return true;
      }
      return false;
    }
    return false;
  };

  // Etichete UI pentru sloturi
  const slotLabel = (slotType) => {
    const key = `sheets.slotLabels.${slotType}`;
    const val = t(key);
    return val === key ? slotType : val;
  };

  // Returnează adevărat doar dacă userul curent poate semna chiar acum (e următorul în ordine)
  const needsMySignature = (training) => {
    if ((training.signatures || []).some((s) => s.signerId === user?.id)) return false;
    const next = getNextRequiredSlot(training);
    if (!next) return false;
    return canFillSlot(next, training);
  };

  // Verifică dacă o instruire e complet semnată (toate slot-urile din matrice umplute).
  // Echivalent al backend `isTrainingFullySigned` pe frontend.
  const isTrainingFullySignedFE = (training) => {
    if (!training) return false;
    return !getNextRequiredSlot(training);
  };

  // Simulează: va fi instruirea complet semnată DUPĂ ce userul curent semnează în acest bulk
  // toate slot-urile pe care le poate umple? Folosit pentru admis-la-lucru: o admitere e
  // „blocată acum" pentru că ILM nu e încă semnat, dar dacă exact eu semnez ILM-ul în acest
  // bulk, admiterea devine validă imediat după. Backend-ul re-verifică oricum (attemptAutoAdmit),
  // dar pre-selectăm corect ca user-ul să nu fie nevoit să facă a doua tură de admiteri separat.
  const wouldBeFullySignedAfterMyBulkSign = (training) => {
    if (!training) return false;
    if (isTrainingFullySignedFE(training)) return true;
    const expected = getExpectedSignerTypes(
      training.course?.type,
      training.employee?.user?.role,
      isTraineeCLM(training),
      !!training._companyHasDG
    );
    if (!expected) return false;
    const sigs = training.signatures || [];
    const traineeUserId = training.employee?.userId;
    const seen = new Set();
    for (const slot of expected) {
      if (slot === 'EMPLOYEE') {
        // Angajatul trebuie să fi semnat deja — eu nu pot semna în locul lui.
        if (!sigs.some((s) => s.signerRole === 'EMPLOYEE' && s.signerId === traineeUserId))
          return false;
        continue;
      }
      if (seen.has(slot)) continue;
      seen.add(slot);
      if (slotIsFilled(slot, sigs, traineeUserId)) continue;
      // Slot ne-semnat — devine umplut în acest bulk DOAR dacă eu îl pot semna.
      if (!canFillSlot(slot, training)) return false;
    }
    return true;
  };

  // Cross-training ordering — atenționare proactivă în UI.
  //   - ILM (non-EMPLOYEE): blocat până IIG (același domeniu) e complet semnat.
  //   - IP/SUPLIMENTARE: blocat DOAR de IIG/ILM care EXISTĂ și nu-s semnate complet. Dacă IIG/ILM
  //     NU sunt atribuite, IP-ul e independent și se poate semna. (Regulă owner: „dacă există
  //     IIG+ILM, IP după ele; dacă nu, IP independent".) Bug anterior: bloca IP și când IIG/ILM
  //     LIPSEAU (`!iig`), ascunzând butonul „Semnează" manual deși angajatul are doar IP-uri.
  // EMPLOYEE pe IIG/ILM e free pass.
  const getCrossTrainingBlockersFE = (training, empTrainings, slotType = null) => {
    const courseType = training?.course?.type || '';
    const isSU = courseType.endsWith('_SU');
    const isILM = courseType.startsWith('ILM_');
    const isIP = courseType.startsWith('IP_') || courseType.startsWith('SUPLIMENTARE_');
    if (!isILM && !isIP) return [];
    const isEmployee = slotType === 'EMPLOYEE';
    if (isILM && isEmployee) return []; // trainee free pass pe ILM
    const find = (type) => (empTrainings || []).find((t) => t.course?.type === type);
    const iigType = isSU ? 'IIG_SU' : 'IIG_SSM';
    const ilmType = isSU ? 'ILM_SU' : 'ILM_SSM';
    const blockers = [];
    if (isILM) {
      const iig = find(iigType);
      if (!iig)
        blockers.push(t('sheets.blockers.typeMissing', { type: iigType.replace('_', ' ') }));
      else if (!isTrainingFullySignedFE(iig))
        blockers.push(t('sheets.blockers.typeNotFullySigned', { type: iigType.replace('_', ' ') }));
    } else if (isIP) {
      // Doar pe IIG/ILM care EXISTĂ. Lipsa lor NU blochează (IP independent).
      const iig = find(iigType);
      if (iig && !isTrainingFullySignedFE(iig))
        blockers.push(t('sheets.blockers.typeNotFullySigned', { type: iigType.replace('_', ' ') }));
      const ilm = find(ilmType);
      if (ilm && !isTrainingFullySignedFE(ilm))
        blockers.push(t('sheets.blockers.typeNotFullySigned', { type: ilmType.replace('_', ' ') }));
    }
    return blockers;
  };

  // Grupăm training-urile per angajat. `needsMySignatureCount` include și testele finale
  // pendente pe acel angajat (din pendingTests) pentru ca filtrul „de semnat de mine"
  // să nu mai dispară un angajat cu fișe semnate dar test neîncă semnat.
  const employeeMap = useMemo(() => {
    const map = {};
    sheets.forEach((t) => {
      const empId = t.employee?.id;
      if (!empId) return;
      if (!map[empId]) {
        map[empId] = {
          employee: t.employee,
          trainings: [],
          totalSigs: 0,
          hasCertificate: false,
          latestCertTrainingId: null,
          latestCertNumber: null,
          needsMySignatureCount: 0,
          pendingTestCount: 0,
        };
      }
      map[empId].trainings.push(t);
      if (t.certificate) {
        map[empId].hasCertificate = true;
        map[empId].latestCertTrainingId = t.id;
        map[empId].latestCertNumber = t.certificate.certificateNumber;
      }
      map[empId].totalSigs += (t.signatures || []).length;
      if (needsMySignature(t)) {
        map[empId].needsMySignatureCount++;
      }
    });
    // Atașăm testele pendente per employee (chiar dacă employee-ul nu are nicio fișă în sheets).
    (pendingTests || []).forEach((pt) => {
      const empId = pt.employeeId;
      if (!empId) return;
      if (!map[empId]) {
        map[empId] = {
          employee: { id: empId },
          trainings: [],
          totalSigs: 0,
          hasCertificate: false,
          latestCertTrainingId: null,
          latestCertNumber: null,
          needsMySignatureCount: 0,
          pendingTestCount: 0,
        };
      }
      map[empId].pendingTestCount++;
      map[empId].needsMySignatureCount++;
    });
    return map;
  }, [sheets, pendingTests, user]);

  const employees = Object.values(employeeMap);

  // Extrage liste unice pentru dropdown-uri
  const departments = useMemo(() => {
    // Toate departamentele firmei (inclusiv cele fără fișe), când s-a selectat o firmă
    if (selectedCompany && companyDepartments.length > 0) {
      return [...new Set(companyDepartments)].sort();
    }
    // Pentru CLM (EMPLOYEE manager) — toate departamentele firmei lui
    if (user?.role === 'EMPLOYEE' && companyDepartments.length > 0) {
      return [...new Set(companyDepartments)].sort();
    }
    return [...new Set(employees.map((e) => e.employee?.department?.name).filter(Boolean))].sort();
  }, [employees, selectedCompany, companyDepartments, user]);

  const jobTitles = useMemo(() => {
    let titles = employees.map((e) => e.employee?.jobTitle).filter(Boolean);
    if (selectedDepartment) {
      titles = employees
        .filter((e) => e.employee?.department?.name === selectedDepartment)
        .map((e) => e.employee?.jobTitle)
        .filter(Boolean);
    }
    return [...new Set(titles)].sort();
  }, [employees, selectedDepartment]);

  const clientCompanies = useMemo(() => {
    return companies.filter((c) => c.type === 'CLIENT');
  }, [companies]);

  // Filtrare
  const filtered = employees.filter((e) => {
    const emp = e.employee;
    const empName = `${emp?.user?.firstName || ''} ${emp?.user?.lastName || ''}`.toLowerCase();
    const dept = emp?.department?.name || '';
    const job = emp?.jobTitle || '';

    if (search && !empName.includes(search.toLowerCase())) return false;
    if (selectedDepartment && dept !== selectedDepartment) return false;
    if (selectedJobTitle && job !== selectedJobTitle) return false;
    if (onlyMyPending && e.needsMySignatureCount === 0) return false;
    return true;
  });

  const stats = {
    total: employees.length,
    withCert: employees.filter((e) => e.hasCertificate).length,
    myPending: employees.filter((e) => e.needsMySignatureCount > 0).length,
    // Numărul TOTAL de teste pendente (per training, nu per angajat) — folosit ca să arăte
    // user-ului că are de semnat ȘI teste, nu doar fișe. Fără un counter separat, semnatarii
    // omiteau testele (sunt afișate doar în rândurile expandate, sub fișe).
    pendingTestsTotal: (pendingTests || []).length,
    pendingTestEmployees: employees.filter((e) => e.pendingTestCount > 0).length,
  };

  // Lista employeeIds care au cel puțin un test pendent — folosită ca să expandăm rândurile lor
  // automat când userul apasă pe stat-ul „Teste de semnat".
  const employeeIdsWithPendingTests = (pendingTests || [])
    .map((pt) => pt.employeeId)
    .filter(Boolean);

  // Scroll la card-ul dedicat de teste de semnat (afișat ca secțiune separată sus, înainte
  // de lista de fișe). Folosit ca handler pentru stat-ul „Teste de semnat".
  const showAllPendingTests = () => {
    if (employeeIdsWithPendingTests.length === 0) return;
    // Asigură că secțiunea de teste e expandată ca să fie vizibilă imediat după scroll.
    setTestsCardExpanded(true);
    setTimeout(() => {
      const el = document.getElementById('pending-tests-card');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  };

  // Deschide fișa în tab nou cu disposition=inline — fără să o salveze în Downloads.
  // Folosește noul endpoint full-sheet care concatenează fișa principală cu fișele postului
  // semnate complet (dacă există). Fallback la endpoint-ul vechi când lipsește employee.id.
  // Blob URL-ul e revocat după 60s ca să nu reținem PDF-ul în memoria browser-ului.
  const handleView = async (empData) => {
    if (!empData.hasCertificate || !empData.latestCertTrainingId) {
      showToast(t('sheets.pdfNotGenerated'), 'error');
      return;
    }
    setViewing(empData.employee.id);
    try {
      const fullSheetUrl = empData.employee?.id
        ? `/job-descriptions/employee/${empData.employee.id}/full-sheet`
        : `/certificates/${empData.latestCertTrainingId}/download?inline=true`;
      const res = await api.get(fullSheetUrl, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      showToast(t('sheets.openSheetError'), 'error');
    } finally {
      setViewing(null);
    }
  };

  const handleDownload = async (empData) => {
    if (!empData.hasCertificate || !empData.latestCertTrainingId) {
      showToast(t('sheets.pdfNotGenerated'), 'error');
      return;
    }
    setDownloading(empData.employee.id);
    try {
      const fullSheetUrl = empData.employee?.id
        ? `/job-descriptions/employee/${empData.employee.id}/full-sheet`
        : `/certificates/${empData.latestCertTrainingId}/download`;
      const res = await api.get(fullSheetUrl, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const empName =
        `${empData.employee?.user?.firstName || ''}-${empData.employee?.user?.lastName || ''}`.toUpperCase();
      a.download = `Fisa-Instruire-${empName}-${empData.latestCertNumber || 'draft'}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      showToast(t('sheets.downloadSheetError'), 'error');
    } finally {
      setDownloading(null);
    }
  };

  const getCourseTypeLabel = (type) => {
    const key = `courses.typeLabelFull.${type}`;
    const val = t(key);
    return val === key ? type : val;
  };

  // Signature status per training: green checkmarks for signed, orange circles for pending
  const renderSigIcons = (training) => {
    const sigs = training.signatures || [];
    const traineeRole = training.employee?.user?.role;
    const expected = getExpectedSignerTypes(
      training.course?.type,
      traineeRole,
      isTraineeCLM(training)
    );

    // SSM și CADRU_PSI pot semna sloturi duplicate (un singur click semnează ambele)
    // Pentru EMPLOYEE/ADMIN/MANAGER, fiecare semnătură consumă un slot.
    const traineeUserId = training.employee?.userId;
    const matchSig = (type, used) =>
      sigs.find((s, idx) => {
        // SSM, CADRU_PSI, ADMIN și MANAGER cu sloturi duplicate sunt umplute de o singură semnătură
        const isReusable =
          type === 'SSM' ||
          type === 'CADRU_PSI' ||
          type === 'ADMIN' ||
          type === 'MANAGER' ||
          type === 'DIRECTOR';
        if (!isReusable && used.has(idx)) return false;
        if (type === 'SSM')
          return (
            ['SSM_INSTRUCTOR', 'SSM_COMPANY_ADMIN'].includes(s.signerRole) ||
            s.signer?.isSSMInstructor
          );
        if (type === 'CADRU_PSI') return s.signer?.isCadruTehnicPSI;
        if (type === 'ADMIN') return s.signerRole === 'COMPANY_ADMIN';
        if (type === 'DIRECTOR') return s.signerRole === 'DIRECTOR_GENERAL';
        if (type === 'MANAGER') {
          // Conducătorul nu poate fi cel instruit însuși — semnătura sa de EMPLOYEE
          // (sau semnătura sa) nu umple slot-ul MANAGER pe propria fișă.
          if (s.signerId === traineeUserId) return false;
          return s.signerRole === 'EMPLOYEE' || s.signerRole === 'MANAGER';
        }
        if (type === 'EMPLOYEE') return s.signerRole === 'EMPLOYEE' && s.signerId === traineeUserId;
        return s.signerRole === type;
      });
    const usedSigs = new Set();

    return (
      <div className="flex gap-1">
        {expected.map((type, i) => {
          const sig = matchSig(type, usedSigs);
          if (sig) {
            usedSigs.add(sigs.indexOf(sig));
            return (
              <div
                key={i}
                title={
                  sig
                    ? `${sig.signer?.firstName || ''} ${sig.signer?.lastName || ''} (${sig.signerRole})`
                    : type
                }
                className="w-5 h-5 rounded-full bg-accent-500 flex items-center justify-center text-white"
              >
                <svg
                  className="w-3 h-3"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={3}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                </svg>
              </div>
            );
          }
          const labelKey = `sheets.slotLabelsTitle.${type}`;
          const translatedLabel = t(labelKey);
          const labelText = translatedLabel === labelKey ? type : translatedLabel;
          return (
            <div
              key={i}
              title={`${labelText} ${t('sheets.slotUnsignedSuffix')}`}
              className="w-5 h-5 rounded-full bg-warning-400 flex items-center justify-center text-white"
            >
              <svg
                className="w-3 h-3"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={3}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
            </div>
          );
        })}
      </div>
    );
  };

  // ── Bulk sign helpers ──
  const allSignableTrainingIds = useMemo(() => {
    const ids = [];
    filtered.forEach((e) =>
      e.trainings.forEach((t) => {
        if (needsMySignature(t)) ids.push(t.id);
      })
    );
    return ids;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtered, user, myManagedDeptIds]);

  // Testele finale pendente pentru angajații din lista filtrată — selecția lor merge separat
  // de fișe pentru că endpoint-ul de bulk nu acceptă teste. Le iterăm individual la submit.
  const allSignableTestTrainingIds = useMemo(() => {
    const filteredEmpIds = new Set(filtered.map((e) => e.employee?.id).filter(Boolean));
    return (pendingTests || [])
      .filter((pt) => filteredEmpIds.has(pt.employeeId))
      .map((pt) => pt.trainingId);
  }, [filtered, pendingTests]);

  const totalSignableCount =
    allSignableTrainingIds.length + allSignableTestTrainingIds.length + jdPending.length;
  const totalSelectedCount =
    selectedTrainingIds.size +
    selectedTestTrainingIds.size +
    selectedAdmissionItems.size +
    jdSelectedIds.size;

  // Helper: pentru un angajat + domeniu (SSM/SU), întoarce lista de „blockers" (motive
  // pentru care admiterea nu se poate face acum). Listă goală = admiterea e validă.
  // Reflectă exact regula backend din `attemptAutoAdmit` (IIG + ILM ambele complete).
  const computeAdmissionBlockers = (e, domain) => {
    const iig = e.trainings.find(
      (t) => t.course?.type === (domain === 'SSM' ? 'IIG_SSM' : 'IIG_SU')
    );
    const ilm = e.trainings.find(
      (t) => t.course?.type === (domain === 'SSM' ? 'ILM_SSM' : 'ILM_SU')
    );
    const iigSlot = iig ? getNextRequiredSlot(iig) : t('sheets.blockers.missingTrainingIIG');
    const ilmSlot = ilm ? getNextRequiredSlot(ilm) : t('sheets.blockers.missingTrainingILM');
    const slotToLabel = (slot) => {
      if (slot === 'SSM') return t('sheets.blockers.sigSSM');
      if (slot === 'CADRU_PSI') return t('sheets.blockers.sigCadruPSI');
      if (slot === 'MANAGER') return t('sheets.blockers.sigCLM');
      if (slot === 'ADMIN') return t('sheets.blockers.sigAdmin');
      if (slot === 'DIRECTOR') return t('sheets.blockers.sigDirector');
      if (slot === 'EMPLOYEE') return t('sheets.blockers.sigEmployee');
      return slot;
    };
    const blockers = [];
    if (!iig) blockers.push(t('sheets.blockers.noIIG', { domain }));
    else if (iigSlot)
      blockers.push(t('sheets.blockers.iigNeeds', { domain, sig: slotToLabel(iigSlot) }));
    if (!ilm) blockers.push(t('sheets.blockers.noILM', { domain }));
    else if (ilmSlot)
      blockers.push(t('sheets.blockers.ilmNeeds', { domain, sig: slotToLabel(ilmSlot) }));
    return blockers;
  };

  // Lista de admiteri „candidate" — angajatul nu e încă admis pe acest domeniu și are AMBELE
  // fișe (IIG + ILM) deschise. Includem aici ȘI admiterile blocate (cu motiv) ca să apară
  // în listă, dar marcate vizual ca neselectabile.
  const eligibleAdmissions = useMemo(() => {
    if (user?.role !== 'COMPANY_ADMIN') return [];
    const items = [];
    filtered.forEach((e) => {
      const emp = e.employee;
      if (!emp || emp.companyId !== user?.companyId) return;
      const empName = `${emp.user?.firstName || ''} ${emp.user?.lastName || ''}`.trim();
      const iigSSM = e.trainings.find((t) => t.course?.type === 'IIG_SSM');
      const iigSU = e.trainings.find((t) => t.course?.type === 'IIG_SU');
      const ilmSSM = e.trainings.find((t) => t.course?.type === 'ILM_SSM');
      const ilmSU = e.trainings.find((t) => t.course?.type === 'ILM_SU');
      if (!emp.admittedSSMBy && iigSSM && ilmSSM) {
        const blockers = computeAdmissionBlockers(e, 'SSM');
        // postBulkEligible: blocată acum DOAR pentru că eu urmează să semnez IIG/ILM în acest
        // bulk → după bulk devine validă. Permite pre-bifare în „semnează toate / selectează tot".
        const postBulkEligible =
          blockers.length > 0 &&
          wouldBeFullySignedAfterMyBulkSign(iigSSM) &&
          wouldBeFullySignedAfterMyBulkSign(ilmSSM);
        // ID-urile fișelor IIG/ILM ale acestui domeniu — folosite la select-all ca să includem
        // admiterea ori de câte ori bulk-ul semnează măcar una dintre ele (backend-ul re-verifică).
        const trainingIds = [iigSSM?.id, ilmSSM?.id].filter(Boolean);
        items.push({
          employeeId: emp.id,
          domain: 'SSM',
          employeeName: empName,
          blockers,
          postBulkEligible,
          trainingIds,
        });
      }
      if (!emp.admittedSUBy && iigSU && ilmSU) {
        const blockers = computeAdmissionBlockers(e, 'SU');
        const postBulkEligible =
          blockers.length > 0 &&
          wouldBeFullySignedAfterMyBulkSign(iigSU) &&
          wouldBeFullySignedAfterMyBulkSign(ilmSU);
        const trainingIds = [iigSU?.id, ilmSU?.id].filter(Boolean);
        items.push({
          employeeId: emp.id,
          domain: 'SU',
          employeeName: empName,
          blockers,
          postBulkEligible,
          trainingIds,
        });
      }
    });
    return items;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtered, user]);

  const unblockedCount = useMemo(
    () => eligibleAdmissions.filter((a) => a.blockers.length === 0).length,
    [eligibleAdmissions]
  );

  const toggleTrainingSelected = (trainingId) => {
    setSelectedTrainingIds((prev) => {
      const next = new Set(prev);
      if (next.has(trainingId)) next.delete(trainingId);
      else next.add(trainingId);
      return next;
    });
  };

  const toggleAdmissionSelected = (employeeId, domain) => {
    const key = `${employeeId}-${domain}`;
    setSelectedAdmissionItems((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleTestSelected = (trainingId) => {
    setSelectedTestTrainingIds((prev) => {
      const next = new Set(prev);
      if (next.has(trainingId)) next.delete(trainingId);
      else next.add(trainingId);
      return next;
    });
  };

  const selectAllSignable = () => {
    setSelectedTrainingIds(new Set(allSignableTrainingIds));
    setSelectedTestTrainingIds(new Set(allSignableTestTrainingIds));
    // Pre-bifează TOATE admiterile candidate (angajați cu IIG+ILM, neadmiși încă, din firma mea).
    // Nu filtrăm după blocaje aici: adminul semnează ILM-ul în acest bulk, iar admis-la-lucru
    // trebuie să intre în ACELAȘI pas, fără o tură separată. Backend-ul (attemptAutoAdmit) e
    // arbitrul final — DUPĂ semnare admite doar angajații cu IIG+ILM complet semnate, restul le
    // raportează „skipped" cu motiv în modalul de rezultat (deci nimeni nu e admis prematur).
    const admissionKeys = (eligibleAdmissions || []).map((a) => `${a.employeeId}-${a.domain}`);
    setSelectedAdmissionItems(new Set(admissionKeys));
    // Include și toate fișele postului pending — bulk-sign le acoperă pe toate cu o singură parolă.
    setJdSelectedIds(new Set(jdPending.map((p) => p.instanceId)));
  };
  const clearBulkSelection = () => {
    setSelectedTrainingIds(new Set());
    setSelectedTestTrainingIds(new Set());
    setSelectedAdmissionItems(new Set());
    setJdSelectedIds(new Set());
  };

  // ── Selecții pe categorie (pentru butoanele de bulk separate: Admiteri / Teste / Fișe / Tot) ──
  // Fiecare setează DOAR categoria lui și golește restul, apoi deschide confirmarea + parola.
  // Submit-ul (submitBulkSign) semnează exact ce e selectat, deci refolosim același flux.
  const startBulkCategory = (selector) => {
    clearBulkSelection();
    selector();
    setShowBulkConfirm(true);
  };
  const selectSheetsOnly = () => setSelectedTrainingIds(new Set(allSignableTrainingIds));
  const selectTestsOnly = () => setSelectedTestTrainingIds(new Set(allSignableTestTrainingIds));
  const selectAdmissionsOnly = () =>
    setSelectedAdmissionItems(
      new Set((eligibleAdmissions || []).map((a) => `${a.employeeId}-${a.domain}`))
    );
  const selectJobDescriptionsOnly = () =>
    setJdSelectedIds(new Set(jdPending.map((p) => p.instanceId)));

  const exitBulkMode = () => {
    setBulkMode(false);
    setSelectedTrainingIds(new Set());
    setSelectedTestTrainingIds(new Set());
    setSelectedAdmissionItems(new Set());
    setJdSelectedIds(new Set());
  };

  const handleBulkSignConfirm = () => {
    setShowBulkConfirm(false);
    setShowBulkPwdModal(true);
  };

  const submitBulkSign = async (password) => {
    setBulkLoading(true);
    try {
      const fisaIds = Array.from(selectedTrainingIds);
      const testIds = Array.from(selectedTestTrainingIds);
      // Parsează admission items din keys `${empId}-${domain}` în array de obiecte.
      const admissionItems = Array.from(selectedAdmissionItems).map((k) => {
        const lastDash = k.lastIndexOf('-');
        return { employeeId: k.slice(0, lastDash), domain: k.slice(lastDash + 1) };
      });

      // 1) Semnează fișele în bulk (dacă există). Trimitem și admissionItems pentru ca
      //    backend-ul să admită EXPLICIT doar pe cei selectați (evităm auto-admit silent).
      let bulkData = { signed: 0, errors: [], skipped: [] };
      if (fisaIds.length > 0 || admissionItems.length > 0) {
        const res = await api.post('/signatures/bulk', {
          trainingIds: fisaIds,
          admissionItems,
          password,
        });
        bulkData = res.data || bulkData;
      }

      // 2) Semnează testele individual cu aceeași parolă — endpoint-ul de bulk nu acceptă teste.
      //    Acumulăm rezultatele într-un format compatibil cu modalul de rezultat.
      let testsSigned = 0;
      const testErrors = [];
      for (const trainingId of testIds) {
        try {
          await api.post('/signatures/test', { trainingId, password });
          testsSigned++;
        } catch (err) {
          const data = err.response?.data;
          if (data?.requiresAdESActivation) {
            // Activarea AdES e necesară — același case ca la /signatures/bulk.
            setShowBulkPwdModal(false);
            setBulkResult({ error: data.error });
            return;
          }
          testErrors.push({ trainingId, error: data?.error || err.message });
        }
      }

      // 3) Semnează fișele postului selectate (același flow AdES, parolă deja unlocked în backend).
      let jdSigned = 0;
      const jdErrors = [];
      const jdIds = Array.from(jdSelectedIds);
      if (jdIds.length > 0) {
        try {
          const res = await api.post('/job-descriptions/bulk-sign', {
            instanceIds: jdIds,
            password,
          });
          jdSigned = res.data?.signed || 0;
          if (Array.isArray(res.data?.errors)) jdErrors.push(...res.data.errors);
        } catch (err) {
          const data = err.response?.data;
          if (data?.requiresAdESActivation) {
            setShowBulkPwdModal(false);
            setBulkResult({ error: data.error });
            return;
          }
          jdErrors.push({ error: data?.error || err.message });
        }
      }

      setBulkResult({
        ...bulkData,
        signed: (bulkData.signed || 0) + testsSigned + jdSigned,
        testsSigned,
        testErrors,
        jdSigned,
        jdErrors,
        errors: [...(bulkData.errors || []), ...testErrors, ...jdErrors],
      });
      setSelectedTrainingIds(new Set());
      setSelectedTestTrainingIds(new Set());
      setSelectedAdmissionItems(new Set());
      setJdSelectedIds(new Set());
      setShowBulkPwdModal(false);
      await Promise.all([fetchSheets(), fetchPendingTests(), fetchJobDescriptionsPending()]);
    } catch (err) {
      const data = err.response?.data;
      if (data?.requiresAdESActivation) {
        setShowBulkPwdModal(false);
        setBulkResult({ error: data.error });
        return;
      }
      throw err;
    } finally {
      setBulkLoading(false);
    }
  };

  // Mapare trainingId → { employeeName, courseTitle, kind } pentru modalul de confirmare.
  // Includem și testele finale selectate (marker `kind: 'test'`) ca să apară în listă.
  const selectedTrainingDetails = useMemo(() => {
    const out = [];
    filtered.forEach((e) => {
      e.trainings.forEach((t) => {
        if (selectedTrainingIds.has(t.id)) {
          out.push({
            trainingId: t.id,
            employeeName:
              `${e.employee?.user?.firstName || ''} ${e.employee?.user?.lastName || ''}`.trim(),
            courseTitle: t.course?.title || '—',
            kind: 'sheet',
          });
        }
      });
    });
    // Adăugăm și testele selectate — folosim numele din employeeMap (fallback la pendingTests data).
    (pendingTests || []).forEach((pt) => {
      if (!selectedTestTrainingIds.has(pt.trainingId)) return;
      const emp = employeeMap[pt.employeeId]?.employee;
      const empName = emp
        ? `${emp.user?.firstName || ''} ${emp.user?.lastName || ''}`.trim()
        : pt.employeeName || '—';
      out.push({
        trainingId: `test:${pt.trainingId}`,
        employeeName: empName,
        courseTitle: (pt.courseTitle || pt.courseType || '—') + t('sheets.testParenSuffix'),
        kind: 'test',
      });
    });
    // Adăugăm și fișele postului selectate ca să fie reflectate în confirmare —
    // anterior numărul total era > decât lista (jdSelectedIds inclus în totalSelectedCount
    // dar nu și în lista vizuală).
    (jdPending || []).forEach((jd) => {
      if (!jdSelectedIds.has(jd.instanceId)) return;
      out.push({
        trainingId: `jd:${jd.instanceId}`,
        employeeName: jd.employeeName,
        courseTitle: t('sheets.jobDescriptionOf', { domain: jd.domain }),
        kind: 'jobDescription',
      });
    });
    return out;
  }, [
    filtered,
    selectedTrainingIds,
    selectedTestTrainingIds,
    pendingTests,
    employeeMap,
    jdPending,
    jdSelectedIds,
  ]);

  // Detaliile admiterilor selectate (pentru lista completă din popup). Cheie = `${empId}-${domain}`.
  const selectedAdmissionDetails = useMemo(() => {
    return [...selectedAdmissionItems].map((key) => {
      const lastDash = key.lastIndexOf('-');
      const empId = key.slice(0, lastDash);
      const domain = key.slice(lastDash + 1);
      const emp = employeeMap[empId]?.employee;
      const empName = emp
        ? `${emp.user?.firstName || ''} ${emp.user?.lastName || ''}`.trim()
        : empId;
      return {
        key,
        employeeName: empName,
        courseTitle: t('sheets.admissionOf', { domain }),
        kind: 'admission',
      };
    });
  }, [selectedAdmissionItems, employeeMap]);

  // Lista completă a tot ce se semnează în bulk (operațiuni de semnare), reflectând `totalSelectedCount`.
  const bulkFullList = useMemo(
    () => [...selectedTrainingDetails, ...selectedAdmissionDetails],
    [selectedTrainingDetails, selectedAdmissionDetails]
  );

  const uniqueSelectedEmployees = useMemo(
    () => new Set(bulkFullList.map((d) => d.employeeName)).size,
    [bulkFullList]
  );

  if (loading) return <Spinner label={t('sheets.loadingSheets')} />;

  return (
    <div className="space-y-6">
      {showBulkPwdModal && (
        <SignPasswordModal
          description={t('sheets.bulkConfirmDesc', { count: totalSelectedCount })}
          onSubmit={submitBulkSign}
          onClose={() => setShowBulkPwdModal(false)}
        />
      )}
      {pendingTestSignTrainingId && (
        <SignPasswordModal
          description={t('sheets.testSignConfirmDesc')}
          onSubmit={submitSignTest}
          onClose={() => setPendingTestSignTrainingId(null)}
        />
      )}
      {pendingAdmit && (
        <SignPasswordModal
          title={t('sheets.admitSignTitle') || 'Admis la lucru'}
          description={
            t('sheets.admitSignDesc', { domain: pendingAdmit.domain }) ||
            `Vei semna electronic decizia de admis la lucru (${pendingAdmit.domain}) cu semnătură electronică avansată (AdES).`
          }
          onSubmit={submitAdmit}
          onClose={() => setPendingAdmit(null)}
        />
      )}
      {pendingBulkAdmitItems && (
        <SignPasswordModal
          title={t('sheets.bulkAdmitTitle') || 'Admis la lucru — bulk'}
          description={
            t('sheets.bulkAdmitDesc', { count: pendingBulkAdmitItems.length }) ||
            `Vei admite la lucru ${pendingBulkAdmitItems.length} angajați (operațiuni SSM/SU) cu semnătură electronică avansată (AdES).`
          }
          onSubmit={submitBulkAdmit}
          onClose={() => setPendingBulkAdmitItems(null)}
        />
      )}
      {blockedAdmissionInfo && (
        <div className="modal-overlay" onClick={() => setBlockedAdmissionInfo(null)}>
          <div className="modal-content max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-warning-100 dark:bg-warning-900/50 rounded-lg flex items-center justify-center flex-shrink-0">
                  <svg
                    className="w-5 h-5 text-warning-600 dark:text-warning-300"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z"
                    />
                  </svg>
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                    {t('sheets.cannotAdmitYet')}
                  </h2>
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    {blockedAdmissionInfo.employeeName} · {blockedAdmissionInfo.domain}
                  </p>
                </div>
              </div>
            </div>
            <div className="modal-body">
              <p className="text-sm text-slate-700 dark:text-slate-200 mb-3">
                {t('sheets.admitBlockedIntro')}
              </p>
              <ul className="space-y-2 mb-3">
                {blockedAdmissionInfo.blockers.map((b, idx) => (
                  <li
                    key={idx}
                    className="flex items-start gap-2 text-sm text-warning-800 dark:text-warning-200"
                  >
                    <svg
                      className="w-4 h-4 mt-0.5 text-warning-500 flex-shrink-0"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
                      />
                    </svg>
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                {t('sheets.admitBlockedHint')}
              </p>
            </div>
            <div className="modal-footer">
              <Button
                variant="primary"
                onClick={() => setBlockedAdmissionInfo(null)}
                className="w-full"
              >
                {t('sheets.understood')}
              </Button>
            </div>
          </div>
        </div>
      )}
      {!embedded && (
        <>
          {companyIdFilter && (
            <Breadcrumbs
              items={[
                { label: t('nav.companies'), href: `/companies/${companyIdFilter}` },
                { label: t('sheets.title') },
              ]}
            />
          )}
          <PageHeader
            title={t('sheets.title')}
            subtitle={t('sheets.subtitle')}
            actions={
              companyIdFilter && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate(`/companies/${companyIdFilter}`)}
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M10 19l-7-7m0 0l7-7m-7 7h18"
                    />
                  </svg>
                  {t('common.back')}
                </Button>
              )
            }
          />
        </>
      )}

      {/* Stat cards — a 4-a card e dedicată testelor pendente (separat de fișe), ca semnatarii
                care apar și ca CLM/SSM/PSI să vadă explicit că au de semnat și teste, nu doar fișe.
                Click pe ea expandează primul angajat cu test pendent + activează filtrul „doar
                de semnat de mine". */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          {
            label: t('sheets.statEmployees'),
            value: stats.total,
            bg: 'bg-primary-50 dark:bg-primary-950/50',
            text: 'text-primary-700 dark:text-primary-300',
            border: 'border-primary-200 dark:border-primary-700',
          },
          {
            label: t('sheets.statWithCert'),
            value: stats.withCert,
            bg: 'bg-accent-50 dark:bg-accent-950/50',
            text: 'text-accent-700 dark:text-accent-300',
            border: 'border-accent-200 dark:border-accent-700',
          },
          {
            label: t('sheets.statMyPending'),
            value: stats.myPending,
            bg: 'bg-warning-50 dark:bg-warning-950/50',
            text: 'text-warning-700 dark:text-warning-300',
            border: 'border-warning-200 dark:border-warning-700',
            onClick: () => setOnlyMyPending(!onlyMyPending),
            cursor: true,
            highlightWhen: onlyMyPending,
            highlightRing: 'ring-warning-400',
          },
          {
            label: t('sheets.statPendingTests') || 'Teste de semnat',
            value: stats.pendingTestsTotal,
            bg: 'bg-danger-50 dark:bg-danger-950/30',
            text: 'text-danger-700 dark:text-danger-300',
            border: 'border-danger-200 dark:border-danger-700',
            onClick: showAllPendingTests,
            cursor: stats.pendingTestsTotal > 0,
            sublabel:
              stats.pendingTestsTotal > 0
                ? t('sheets.atNEmployees', { count: stats.pendingTestEmployees })
                : null,
          },
        ].map((s, i) => (
          <div
            key={i}
            className={`rounded-xl border-2 p-4 ${s.bg} ${s.text} ${s.border} ${s.cursor ? 'cursor-pointer hover:shadow-md transition' : ''} ${s.highlightWhen ? `ring-2 ${s.highlightRing || 'ring-primary-400'}` : ''}`}
            onClick={s.onClick}
          >
            <div className="text-2xl font-bold">{s.value}</div>
            <div className="text-xs font-medium mt-0.5">{s.label}</div>
            {s.sublabel && <div className="text-[10px] mt-1 opacity-75">{s.sublabel}</div>}
          </div>
        ))}
      </div>

      {/* Bulk sign mode toggle — pentru oricine semnează regulat (admin, SSM, CLM, PSI/SSMInst flag) */}
      {(((user?.role === 'COMPANY_ADMIN' ||
        user?.role === 'SSM_COMPANY_ADMIN' ||
        user?.role === 'SSM_INSTRUCTOR' ||
        (user?.role === 'EMPLOYEE' &&
          (user?.isSSMInstructor ||
            user?.isCadruTehnicPSI ||
            user?.isCLM === true ||
            myManagedDeptIds?.length > 0))) &&
        stats.myPending > 0) ||
        // FP de semnat: backend-ul (getPendingForMe) a autorizat deja semnatarul — admin,
        // împuternicit (AdminMandate) sau delegat, indiferent de rol. Arătăm bara chiar dacă
        // nu sunt fișe de instruire de semnat (înainte bara era ascunsă când myPending===0).
        jdPending.length > 0) && (
        <div className="flex flex-wrap items-center justify-between gap-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-800 rounded-xl px-4 py-3">
          <div className="flex items-center gap-3">
            <svg
              className="w-5 h-5 text-slate-500 dark:text-slate-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
              />
            </svg>
            <div>
              <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                {t('sheets.bulkModeTitle')}
              </p>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                {bulkMode
                  ? `${t('sheets.bulkAvailable', { count: allSignableTrainingIds.length })}${allSignableTestTrainingIds.length > 0 ? t('sheets.plusNTests', { count: allSignableTestTrainingIds.length }) : ''}${jdPending.length > 0 ? t('sheets.plusNJobDescriptions', { count: jdPending.length }) : ''}`
                  : t('sheets.bulkSignDesc')}
              </p>
            </div>
          </div>
          {bulkMode ? (
            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={selectAllSignable}
                disabled={totalSignableCount === 0}
              >
                {t('sheets.selectAllCount', { count: totalSignableCount })}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={clearBulkSelection}
                disabled={totalSelectedCount === 0}
              >
                {t('sheets.deselectAll')}
              </Button>
              <Button variant="secondary" size="sm" onClick={exitBulkMode}>
                {t('sheets.exit')}
              </Button>
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {/* Bulk pe categorie — fiecare semnează DOAR categoria lui, cu o singură parolă.
                                „Tot" le face pe toate. „Selectează manual" deschide modul de bifare per rând. */}
              {allSignableTrainingIds.length > 0 && (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => startBulkCategory(selectSheetsOnly)}
                >
                  {t('sheets.bulkSheetsBtn', { count: allSignableTrainingIds.length })}
                </Button>
              )}
              {allSignableTestTrainingIds.length > 0 && (
                <Button
                  variant="warning"
                  size="sm"
                  onClick={() => startBulkCategory(selectTestsOnly)}
                >
                  {t('sheets.bulkTestsBtn', { count: allSignableTestTrainingIds.length })}
                </Button>
              )}
              {(eligibleAdmissions || []).length > 0 && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => startBulkCategory(selectAdmissionsOnly)}
                >
                  {t('sheets.bulkAdmissionsBtn', { count: eligibleAdmissions.length })}
                </Button>
              )}
              {jdPending.length > 0 && (
                <Button
                  variant="accent"
                  size="sm"
                  onClick={() => startBulkCategory(selectJobDescriptionsOnly)}
                >
                  {t('sheets.bulkJobDescriptionsBtn', { count: jdPending.length })}
                </Button>
              )}
              {/* „Tot" — doar dacă există ≥2 categorii (altfel e identic cu butonul de categorie). */}
              {[
                allSignableTrainingIds.length > 0,
                allSignableTestTrainingIds.length > 0,
                (eligibleAdmissions || []).length > 0,
                jdPending.length > 0,
              ].filter(Boolean).length >= 2 && (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => {
                    selectAllSignable();
                    setShowBulkConfirm(true);
                  }}
                >
                  {t('sheets.bulkAllBtn', { count: totalSignableCount })}
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setBulkMode(true)}
                disabled={totalSignableCount === 0}
              >
                {t('sheets.activateSelectMode')}
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Bulk admit la lucru — doar pentru COMPANY_ADMIN, vizibil când există admiteri eligibile */}
      {user?.role === 'COMPANY_ADMIN' && eligibleAdmissions.length > 0 && (
        <div className="bg-accent-50 dark:bg-accent-950/50 border border-accent-200 dark:border-accent-700 rounded-xl px-4 py-3 space-y-2">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <svg
                className="w-5 h-5 text-accent-600 dark:text-accent-300"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <div>
                <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                  {t('sheets.bulkAdmitTitle')}
                </p>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  {t('sheets.availableCount', { count: unblockedCount })}
                  {eligibleAdmissions.length - unblockedCount > 0 && (
                    <span>
                      {' '}
                      ·{' '}
                      <span className="text-warning-700 dark:text-warning-300">
                        {t('sheets.blockedCount', {
                          count: eligibleAdmissions.length - unblockedCount,
                        })}
                      </span>
                    </span>
                  )}
                </p>
              </div>
            </div>
            <Button
              variant="accent"
              size="sm"
              onClick={() => {
                // default: doar cele neblocate bifate
                const okKeys = new Set(
                  eligibleAdmissions
                    .filter((a) => a.blockers.length === 0)
                    .map((a) => `${a.employeeId}-${a.domain}`)
                );
                setSelectedAdmitKeys(okKeys);
                setBulkAdmitOpen(true);
              }}
            >
              {t('sheets.selectAdmissions')}
            </Button>
          </div>
        </div>
      )}

      {/* Rezultatul ultimului bulk admit — cu detalii per item (motive skip) */}
      {bulkAdmitResult && (
        <div
          className={`rounded-xl border px-4 py-3 ${
            bulkAdmitResult.admitted > 0
              ? 'bg-accent-50 dark:bg-accent-950/50 border-accent-200 dark:border-accent-700'
              : 'bg-warning-50 dark:bg-warning-950/30 border-warning-200 dark:border-warning-700'
          }`}
        >
          <div className="flex items-start justify-between gap-3 mb-2">
            <div>
              <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                {t('sheets.admitResultSummary', {
                  admitted: bulkAdmitResult.admitted,
                  skipped: bulkAdmitResult.skipped,
                })}
              </p>
              {bulkAdmitResult.message && (
                <p className="text-xs text-slate-600 dark:text-slate-300 mt-1">
                  {bulkAdmitResult.message}
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={() => setBulkAdmitResult(null)}
              className="text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 text-xs"
            >
              {t('common.close')}
            </button>
          </div>
          {bulkAdmitResult.details?.skipped?.length > 0 && (
            <div className="mt-2">
              <p className="text-xs font-semibold text-warning-800 dark:text-warning-300 mb-1">
                {t('sheets.skipReasons')}
              </p>
              <ul className="space-y-1 max-h-48 overflow-y-auto">
                {bulkAdmitResult.details.skipped.map((s, idx) => (
                  <li
                    key={idx}
                    className="text-xs flex flex-wrap gap-1 text-slate-700 dark:text-slate-200"
                  >
                    <span className="font-medium">{s.employeeName}</span>
                    <span className="text-slate-400">·</span>
                    <span className="uppercase text-[10px] tracking-wide px-1.5 py-0.5 rounded bg-slate-200 dark:bg-slate-700">
                      {s.domain}
                    </span>
                    <span className="text-slate-500 dark:text-slate-400">→</span>
                    <span className="text-warning-700 dark:text-warning-300">
                      {s.reasonLabel || s.reason}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Modal: listă admiteri cu checkbox-uri */}
      {bulkAdmitOpen && (
        <div className="modal-overlay" onClick={() => setBulkAdmitOpen(false)}>
          <div className="modal-content max-w-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-accent-100 dark:bg-accent-900/50 rounded-lg flex items-center justify-center flex-shrink-0">
                  <svg
                    className="w-5 h-5 text-accent-600 dark:text-accent-300"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                    {t('sheets.bulkAdmitChooseTitle')}
                  </h2>
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    {t('sheets.bulkAdmitChooseDesc')}
                  </p>
                </div>
              </div>
            </div>
            <div className="modal-body">
              <div className="flex items-center justify-between mb-2 text-xs">
                <button
                  type="button"
                  onClick={() =>
                    setSelectedAdmitKeys(
                      new Set(
                        eligibleAdmissions
                          .filter((a) => a.blockers.length === 0)
                          .map((a) => `${a.employeeId}-${a.domain}`)
                      )
                    )
                  }
                  className="text-primary-600 dark:text-primary-300 hover:underline"
                >
                  {t('sheets.selectAllAvailable', { count: unblockedCount })}
                </button>
                <button
                  type="button"
                  onClick={() => setSelectedAdmitKeys(new Set())}
                  className="text-slate-500 dark:text-slate-400 hover:underline"
                >
                  {t('sheets.deselectAllFull')}
                </button>
              </div>
              <ul className="divide-y divide-slate-200 dark:divide-slate-700 max-h-80 overflow-y-auto">
                {eligibleAdmissions.map((a) => {
                  const key = `${a.employeeId}-${a.domain}`;
                  const checked = selectedAdmitKeys.has(key);
                  const isBlocked = a.blockers.length > 0;
                  return (
                    <li key={key}>
                      {isBlocked ? (
                        <button
                          type="button"
                          onClick={() => setBlockedAdmissionInfo(a)}
                          className="w-full flex items-center gap-3 py-2 px-1 text-left hover:bg-slate-50 dark:hover:bg-slate-800 rounded cursor-help opacity-60"
                          title={t('sheets.clickToSeeMissing')}
                        >
                          <svg
                            className="w-4 h-4 text-warning-500 flex-shrink-0"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={2}
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z"
                            />
                          </svg>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-slate-700 dark:text-slate-200 truncate">
                              {a.employeeName}
                            </p>
                            <p className="text-xs text-warning-600 dark:text-warning-300">
                              {t('sheets.blockedClickDetails', { domain: a.domain })}
                            </p>
                          </div>
                        </button>
                      ) : (
                        <label className="flex items-center gap-3 py-2 px-1 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800 rounded">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(e) => {
                              setSelectedAdmitKeys((prev) => {
                                const next = new Set(prev);
                                if (e.target.checked) next.add(key);
                                else next.delete(key);
                                return next;
                              });
                            }}
                            className="h-4 w-4 text-accent-600 focus:ring-accent-500 border-slate-300 dark:border-slate-700 rounded"
                          />
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">
                              {a.employeeName}
                            </p>
                            <p className="text-xs text-slate-500 dark:text-slate-400">
                              {t('sheets.domainLabel', { domain: a.domain })}
                            </p>
                          </div>
                        </label>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
            <div className="modal-footer flex gap-2">
              <Button variant="ghost" onClick={() => setBulkAdmitOpen(false)}>
                {t('common.cancel')}
              </Button>
              <Button
                variant="accent"
                disabled={selectedAdmitKeys.size === 0}
                onClick={() => {
                  const items = eligibleAdmissions.filter((a) =>
                    selectedAdmitKeys.has(`${a.employeeId}-${a.domain}`)
                  );
                  setBulkAdmitOpen(false);
                  setPendingBulkAdmitItems(items);
                }}
                className="flex-1"
              >
                {selectedAdmitKeys.size === 1
                  ? t('sheets.continueWithOne', { count: selectedAdmitKeys.size })
                  : t('sheets.continueWithMany', { count: selectedAdmitKeys.size })}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Filters — grid compact, unul lângă altul */}
      <Card>
        <div
          className={`grid grid-cols-1 sm:grid-cols-2 gap-3 ${isSSMRole && clientCompanies.length > 0 && !embedded ? 'lg:grid-cols-4' : 'lg:grid-cols-3'}`}
        >
          <input
            type="text"
            placeholder={t('sheets.searchPlaceholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input w-full"
          />
          {isSSMRole && clientCompanies.length > 0 && !embedded && (
            <select
              value={selectedCompany}
              onChange={(e) => {
                setSelectedCompany(e.target.value);
                setSelectedDepartment('');
                setSelectedJobTitle('');
              }}
              className="select w-full"
            >
              <option value="">{t('sheets.allCompanies')}</option>
              {clientCompanies.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          )}
          <select
            value={selectedDepartment}
            onChange={(e) => {
              setSelectedDepartment(e.target.value);
              setSelectedJobTitle('');
            }}
            className="select w-full"
            disabled={departments.length === 0}
          >
            <option value="">{t('sheets.allDepartments')}</option>
            {departments.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
          <select
            value={selectedJobTitle}
            onChange={(e) => setSelectedJobTitle(e.target.value)}
            className="select w-full"
            disabled={jobTitles.length === 0}
          >
            <option value="">{t('sheets.allJobs')}</option>
            {jobTitles.map((j) => (
              <option key={j} value={j}>
                {j}
              </option>
            ))}
          </select>
        </div>
        {((!embedded && selectedCompany) ||
          selectedDepartment ||
          selectedJobTitle ||
          onlyMyPending ||
          search) && (
          <div className="mt-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                if (!embedded) setSelectedCompany('');
                setSelectedDepartment('');
                setSelectedJobTitle('');
                setOnlyMyPending(false);
                setSearch('');
              }}
            >
              {t('common.clearFilters')}
            </Button>
          </div>
        )}
        {onlyMyPending && (
          <div className="mt-2 text-xs text-warning-600 dark:text-warning-300 font-medium flex items-center gap-1">
            <svg
              className="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
              />
            </svg>
            {t('sheets.onlyMyPending')} ({stats.myPending})
          </div>
        )}
      </Card>

      {/* Fișe ale postului care îmi așteaptă semnătura — POZIȚIONAT fix deasupra cardului
                „Teste de semnat" pentru consistență vizuală cu fluxul de semnare. Integrate
                complet în bulk-sign unificat (un singur prompt de parolă pentru tot). */}
      <JobDescriptionPendingList
        items={jdPending}
        selectedIds={jdSelectedIds}
        onToggle={toggleJdSelection}
        bulkMode={bulkMode}
      />

      {/* Card dedicat „Teste de semnat" — separat complet de lista de fișe.
                Testele finale (IIG/ILM SSM/SU) au flux propriu de semnare distinct de fișe.
                Semnatarii (CLM/SSM/PSI) le omiteau anterior pentru că apăreau doar în secțiunile
                expandate sub fișe. Aici sunt grupate per angajat cu buton de semnare directă. */}
      {stats.pendingTestsTotal > 0 &&
        (() => {
          // Grupăm pendingTests per angajat ca să apară structurat în card.
          const testsByEmp = new Map();
          (pendingTests || []).forEach((pt) => {
            if (!pt.employeeId) return;
            if (!testsByEmp.has(pt.employeeId)) testsByEmp.set(pt.employeeId, []);
            testsByEmp.get(pt.employeeId).push(pt);
          });
          return (
            <Card
              id="pending-tests-card"
              className="border-2 border-danger-200 dark:border-danger-700 bg-danger-50/40 dark:bg-danger-950/20"
            >
              {/* Header clickabil — toggle expandare. Mai compact când e colapsat. */}
              <button
                type="button"
                onClick={() => setTestsCardExpanded((v) => !v)}
                className="w-full flex items-center justify-between gap-3 flex-wrap text-left"
              >
                <div className="flex items-center gap-3 min-w-0 flex-wrap">
                  <div className="w-10 h-10 rounded-full bg-danger-100 dark:bg-danger-900/40 flex items-center justify-center flex-shrink-0">
                    <svg
                      className="w-5 h-5 text-danger-600 dark:text-danger-300"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25z"
                      />
                    </svg>
                  </div>
                  <div className="min-w-0">
                    <h2 className="font-bold text-danger-900 dark:text-danger-200 text-base">
                      {t('sheets.pendingTestsHeader', { count: stats.pendingTestsTotal })}
                    </h2>
                    <p className="text-xs text-danger-700 dark:text-danger-300 mt-0.5">
                      {testsCardExpanded
                        ? t('sheets.pendingTestsExpandedHint')
                        : t('jobDescription.collapsedHint', 'click pentru a extinde')}
                    </p>
                  </div>
                </div>
                <svg
                  className={`w-5 h-5 text-danger-600 dark:text-danger-300 flex-shrink-0 transition-transform ${testsCardExpanded ? 'rotate-180' : ''}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M19.5 8.25l-7.5 7.5-7.5-7.5"
                  />
                </svg>
              </button>
              {testsCardExpanded && (
                <ul className="space-y-3 mt-3">
                  {[...testsByEmp.entries()].map(([empId, tests]) => {
                    const empData = employeeMap[empId];
                    const emp = empData?.employee;
                    const empName = emp
                      ? `${emp.user?.firstName || ''} ${emp.user?.lastName || ''}`.trim()
                      : tests[0]?.employeeName || '—';
                    const companyName = emp?.company?.name || tests[0]?.companyName || null;
                    return (
                      <li
                        key={empId}
                        className="rounded-lg border border-danger-200 dark:border-danger-800 bg-white dark:bg-slate-900 px-3 py-2.5"
                      >
                        <div className="flex items-baseline justify-between gap-3 mb-1.5">
                          <p className="font-semibold text-slate-900 dark:text-slate-100 text-sm truncate">
                            {empName}
                            {companyName && isSSMRole && (
                              <span className="ml-2 text-xs text-slate-500 dark:text-slate-400 font-normal">
                                · {companyName}
                              </span>
                            )}
                          </p>
                          <span className="text-xs text-danger-700 dark:text-danger-300 font-medium flex-shrink-0">
                            {tests.length === 1
                              ? t('sheets.nTestsOne', { count: tests.length })
                              : t('sheets.nTestsMany', { count: tests.length })}
                          </span>
                        </div>
                        <ul className="space-y-1.5">
                          {tests.map((pt) => {
                            const ctype = pt.courseType || '';
                            const isIIG = ctype.startsWith('IIG_');
                            const isSU = ctype.endsWith('_SU');
                            const signerLabel = isIIG
                              ? isSU
                                ? t('common.badges.psi')
                                : t('sheets.slotLabelsTitle.SSM')
                              : 'CLM';
                            // Tipul testului ca etichetă scurtă pentru badge: „IIG SSM", „ILM SU", etc.
                            const typeLabel = ctype ? ctype.replace('_', ' ') : '';
                            const isTestSelected = selectedTestTrainingIds.has(pt.trainingId);
                            const scoreOK = (pt.score ?? 0) >= 80;
                            // Cross-training blockers — testul pe ILM cere IIG complet semnat înainte.
                            // Construim un mock-training cu doar course.type ca să refolosim helper-ul.
                            const xtBlockers = !isIIG
                              ? getCrossTrainingBlockersFE(
                                  { course: { type: ctype } },
                                  empData?.trainings || [],
                                  'MANAGER'
                                )
                              : [];
                            const isBlocked = xtBlockers.length > 0;
                            return (
                              <li
                                key={pt.trainingId}
                                className="flex items-center justify-between gap-2 text-xs"
                              >
                                <div className="min-w-0 flex-1 flex items-center gap-2 flex-wrap">
                                  {bulkMode && (
                                    <input
                                      type="checkbox"
                                      className="h-3.5 w-3.5 text-primary-600 rounded cursor-pointer flex-shrink-0"
                                      checked={isTestSelected}
                                      onChange={() => toggleTestSelected(pt.trainingId)}
                                      aria-label={t('sheets.selectTestAria', {
                                        title: pt.courseTitle || ctype,
                                      })}
                                    />
                                  )}
                                  {/* Badge tip test — albastru pentru SSM, roșu pentru SU. Vizibil la prima vedere
                                                                ca semnatarul să distingă rapid IIG SSM vs IIG SU etc., chiar dacă titlurile
                                                                de curs nu menționează domeniul explicit. */}
                                  {typeLabel && (
                                    <span
                                      className={`text-[10px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wide ${isSU ? 'bg-danger-100 dark:bg-danger-950/50 text-danger-700 dark:text-danger-300' : 'bg-primary-100 dark:bg-primary-950/50 text-primary-700 dark:text-primary-300'}`}
                                    >
                                      {typeLabel}
                                    </span>
                                  )}
                                  <span className="text-slate-700 dark:text-slate-200 font-medium">
                                    {pt.courseTitle || ctype}
                                  </span>
                                  <span className="text-slate-500 dark:text-slate-400">
                                    {t('sheets.asSigner', { role: signerLabel })}
                                  </span>
                                  {pt.score != null && (
                                    <span
                                      className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${scoreOK ? 'bg-accent-100 dark:bg-accent-950/50 text-accent-700 dark:text-accent-300' : 'bg-danger-100 dark:bg-danger-950/50 text-danger-700 dark:text-danger-300'}`}
                                    >
                                      {t('sheets.scoreValue', { score: pt.score })}
                                    </span>
                                  )}
                                </div>
                                <div className="flex items-center gap-1.5 flex-shrink-0">
                                  {/* Buton „ochi": deschide modal cu testul + răspunsurile date de angajat
                                                                (verde = corect, roșu = greșit), aliniat cu vederea de pe SignaturePage. */}
                                  <button
                                    type="button"
                                    onClick={() =>
                                      openTestView(
                                        pt.trainingId,
                                        pt.courseTitle || ctype,
                                        pt.employeeName
                                      )
                                    }
                                    title={t('sheets.viewQuestionsTooltip')}
                                    className="p-1.5 text-slate-500 dark:text-slate-400 hover:text-primary-600 dark:hover:text-primary-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded transition-colors"
                                  >
                                    <svg
                                      className="w-4 h-4"
                                      fill="none"
                                      viewBox="0 0 24 24"
                                      stroke="currentColor"
                                      strokeWidth={2}
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"
                                      />
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                      />
                                    </svg>
                                  </button>
                                  {/* Dacă există blocaje cross-training (ex: ILM înainte de IIG), afișăm
                                                                un text de avertizare în loc de butonul „Semnează test". */}
                                  {isBlocked ? (
                                    <span
                                      className="text-[10px] text-warning-700 dark:text-warning-300 font-medium"
                                      title={xtBlockers.join('; ')}
                                    >
                                      {t('sheets.blockedPrefix', { reason: xtBlockers[0] })}
                                    </span>
                                  ) : (
                                    !bulkMode && (
                                      <Button
                                        variant="warning"
                                        size="sm"
                                        onClick={() => handleSignTest(pt.trainingId)}
                                      >
                                        {t('sheets.signTest')}
                                      </Button>
                                    )
                                  )}
                                </div>
                              </li>
                            );
                          })}
                        </ul>
                      </li>
                    );
                  })}
                </ul>
              )}
            </Card>
          );
        })()}

      {filtered.length === 0 ? (
        <EmptyState
          icon={
            <svg
              className="w-6 h-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25z"
              />
            </svg>
          }
          title={t('sheets.empty')}
          description={
            onlyMyPending
              ? t('sheets.noSheetsToSign')
              : sheets.length === 0
                ? t('sheets.emptyNoSheets')
                : t('sheets.emptyFiltered')
          }
        />
      ) : (
        <div className="space-y-3">
          {filtered.map((empData) => {
            const emp = empData.employee;
            const empId = emp?.id;
            // În modul bulk, expandăm automat toți angajații cu fișe de semnat de noi.
            // După ce nu mai au fișe (semnate complet), click-ul manual rămâne funcțional
            // pentru vizualizarea istoricului (altfel rândul rămâne închis până la refresh).
            const autoExpand = bulkMode && empData.needsMySignatureCount > 0;
            const isExpanded = autoExpand || expandedEmployee === empId;
            const empName = `${emp?.user?.firstName || ''} ${emp?.user?.lastName || ''}`;
            const ssmTrainings = empData.trainings.filter((t) => t.course?.type?.endsWith('_SSM'));
            const suTrainings = empData.trainings.filter((t) => t.course?.type?.endsWith('_SU'));
            const isDownloading = downloading === empId;
            const isViewing = viewing === empId;
            // Verifică dacă admin-ul are admis-la-lucru de făcut pentru acest angajat
            const userIsAdminOfEmp =
              user?.role === 'COMPANY_ADMIN' && emp?.companyId === user?.companyId;
            const ilmSSM = empData.trainings.find((t) => t.course?.type === 'ILM_SSM');
            const ilmSU = empData.trainings.find((t) => t.course?.type === 'ILM_SU');
            const ilmSSMComplete = ilmSSM && !getNextRequiredSlot(ilmSSM);
            const ilmSUComplete = ilmSU && !getNextRequiredSlot(ilmSU);
            const hasAdmitPending =
              userIsAdminOfEmp &&
              ((!emp?.admittedSSMBy && ilmSSMComplete) || (!emp?.admittedSUBy && ilmSUComplete));
            const hasPending = empData.needsMySignatureCount > 0 || hasAdmitPending;

            // Modul bulk: toate elementele de semnat ale acestui angajat (fișe + teste + FP +
            // admiteri), pentru checkbox-ul „master" de pe rând care le bifează pe toate odată.
            const empSheetIds = empData.trainings
              .filter((t) => needsMySignature(t))
              .map((t) => t.id);
            const empTestIds = (pendingTests || [])
              .filter((pt) => pt.employeeId === empId)
              .map((pt) => pt.trainingId);
            const empJdIds = (jdPending || [])
              .filter((jd) => jd.employeeId === empId)
              .map((jd) => jd.instanceId);
            const empAdmissionKeys = (eligibleAdmissions || [])
              .filter((a) => a.employeeId === empId)
              .map((a) => `${a.employeeId}-${a.domain}`);
            const empSignableTotal =
              empSheetIds.length + empTestIds.length + empJdIds.length + empAdmissionKeys.length;
            const empAllSelected =
              empSignableTotal > 0 &&
              empSheetIds.every((id) => selectedTrainingIds.has(id)) &&
              empTestIds.every((id) => selectedTestTrainingIds.has(id)) &&
              empJdIds.every((id) => jdSelectedIds.has(id)) &&
              empAdmissionKeys.every((k) => selectedAdmissionItems.has(k));
            const toggleEmpAll = (ev) => {
              ev.stopPropagation();
              const check = !empAllSelected;
              const upd = (ids) => (prev) => {
                const n = new Set(prev);
                ids.forEach((id) => (check ? n.add(id) : n.delete(id)));
                return n;
              };
              setSelectedTrainingIds(upd(empSheetIds));
              setSelectedTestTrainingIds(upd(empTestIds));
              setJdSelectedIds(upd(empJdIds));
              setSelectedAdmissionItems(upd(empAdmissionKeys));
            };

            return (
              <Card
                key={empId}
                id={`emp-row-${empId}`}
                className={`overflow-hidden ${hasPending ? 'border-warning-300 dark:border-warning-700 border-2' : ''}`}
              >
                {/* Rândul principal */}
                <div
                  className={`flex items-center justify-between ${autoExpand ? '' : 'cursor-pointer'}`}
                  onClick={() => {
                    // Click blocat doar când rândul e auto-expandat în bulk (semnături pendinte).
                    // Altfel (inclusiv bulk + niciun pending după semnare) permitem toggle manual.
                    if (autoExpand) return;
                    setExpandedEmployee((prev) => (prev === empId ? null : empId));
                  }}
                >
                  <div className="flex items-center gap-4 flex-1 min-w-0">
                    {bulkMode && empSignableTotal > 0 && (
                      <input
                        type="checkbox"
                        checked={empAllSelected}
                        onChange={toggleEmpAll}
                        onClick={(e) => e.stopPropagation()}
                        title={t('sheets.selectAllForUser')}
                        aria-label={t('sheets.selectAllForUser')}
                        className="h-5 w-5 text-primary-600 rounded border-slate-300 dark:border-slate-700 cursor-pointer shrink-0"
                      />
                    )}
                    <div className="w-10 h-10 rounded-full bg-primary-100 dark:bg-primary-900/50 flex items-center justify-center shrink-0">
                      <span className="text-primary-700 dark:text-primary-300 font-bold text-sm">
                        {emp?.user?.firstName?.[0] || ''}
                        {emp?.user?.lastName?.[0] || ''}
                      </span>
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-slate-900 dark:text-slate-100 text-sm truncate">
                          {empName}
                        </p>
                        {hasPending && (
                          <Badge color="yellow" className="text-xs">
                            {empData.needsMySignatureCount > 0 &&
                              t('sheets.toSignCount', { count: empData.needsMySignatureCount })}
                            {empData.needsMySignatureCount > 0 && hasAdmitPending && ' · '}
                            {hasAdmitPending && t('sheets.admitAtWork')}
                          </Badge>
                        )}
                        {!isExpanded && (
                          <span className="text-xs text-slate-500 dark:text-slate-400 font-normal whitespace-nowrap">
                            {t('jobDescription.collapsedHint', 'click pentru a extinde')}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                        <span>{emp?.jobTitle || '—'}</span>
                        {emp?.department?.name && (
                          <>
                            <span className="text-slate-300 dark:text-slate-600">|</span>
                            <span>{emp.department.name}</span>
                          </>
                        )}
                        {emp?.company?.name && isSSMRole && (
                          <>
                            <span className="text-slate-300 dark:text-slate-600">|</span>
                            <span>{emp.company.name}</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    {ssmTrainings.length > 0 && (
                      <Badge color="blue">
                        {t('sheets.ssmCount', { count: ssmTrainings.length })}
                      </Badge>
                    )}
                    {suTrainings.length > 0 && (
                      <Badge color="red">
                        {t('sheets.suCount', { count: suTrainings.length })}
                      </Badge>
                    )}
                    {/* Badge informativ „teste de semnat" pe rând — click scrollează la
                                            card-ul dedicat de sus (id="pending-tests-card"), unde semnezi direct. */}
                    {(() => {
                      const nTests = (pendingTests || []).filter(
                        (pt) => pt.employeeId === empId
                      ).length;
                      if (nTests === 0) return null;
                      return (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            showAllPendingTests();
                          }}
                          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-warning-100 dark:bg-warning-900/50 text-warning-800 dark:text-warning-200 hover:bg-warning-200 dark:hover:bg-warning-900 transition-colors"
                          title={t('sheets.pendingTestsBadgeTooltip')}
                        >
                          <svg
                            className="w-3.5 h-3.5"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={2}
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                            />
                          </svg>
                          {nTests === 1
                            ? t('sheets.nTestsToSignOne', { count: nTests })
                            : t('sheets.nTestsToSignMany', { count: nTests })}
                        </button>
                      );
                    })()}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleView(empData);
                      }}
                      disabled={!empData.hasCertificate || isViewing || isDownloading}
                      loading={isViewing}
                      title={t('sheets.viewSheetTooltip')}
                    >
                      <svg
                        className="w-3.5 h-3.5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                      </svg>
                      {t('sheets.viewSheet')}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownload(empData);
                      }}
                      disabled={!empData.hasCertificate || isDownloading || isViewing}
                      loading={isDownloading}
                    >
                      <svg
                        className="w-3.5 h-3.5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3"
                        />
                      </svg>
                      {t('sheets.fileAction')}
                    </Button>
                    <svg
                      className={`w-5 h-5 text-slate-400 dark:text-slate-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M19.5 8.25l-7.5 7.5-7.5-7.5"
                      />
                    </svg>
                  </div>
                </div>

                {/* Detalii expandate */}
                {isExpanded && (
                  <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-800 overflow-x-auto -mx-3 px-3">
                    {/* Lista de teste de semnat per angajat a fost MUTATĂ într-un Card dedicat
                                            în partea de sus (id="pending-tests-card"). Aici rămâne doar tabelul de fișe. */}
                    <table className="w-full text-sm min-w-[640px]">
                      <thead>
                        <tr className="text-xs text-slate-500 dark:text-slate-400 uppercase">
                          {bulkMode && <th className="pb-2 w-8"></th>}
                          <th className="text-left pb-2 font-medium">{t('sheets.trainingCol')}</th>
                          <th className="text-left pb-2 font-medium">{t('sheets.typeCol')}</th>
                          <th className="text-left pb-2 font-medium">{t('sheets.dateCol')}</th>
                          <th className="text-left pb-2 font-medium">{t('sheets.scoreCol')}</th>
                          <th className="text-left pb-2 font-medium">
                            {t('sheets.signaturesCol')}
                          </th>
                          <th className="text-right pb-2 font-medium">{t('sheets.actionsCol')}</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                        {empData.trainings.map((training) => {
                          const isSU = training.course?.type?.endsWith('_SU');
                          const myPending = needsMySignature(training);
                          const isSelected = selectedTrainingIds.has(training.id);
                          return (
                            <tr
                              key={training.id}
                              className={`hover:bg-slate-50 dark:hover:bg-slate-800 ${myPending ? 'bg-warning-50 dark:bg-warning-950/50' : ''} ${isSelected ? 'bg-primary-50 dark:bg-primary-950/50' : ''}`}
                            >
                              {bulkMode && (
                                <td className="py-2">
                                  {myPending ? (
                                    <input
                                      type="checkbox"
                                      checked={isSelected}
                                      onChange={() => toggleTrainingSelected(training.id)}
                                      className="h-4 w-4 text-primary-600 rounded border-slate-300 dark:border-slate-700 cursor-pointer"
                                    />
                                  ) : (
                                    <span
                                      className="text-slate-300 dark:text-slate-600"
                                      title={t('sheets.cannotSignThisSheet')}
                                    >
                                      —
                                    </span>
                                  )}
                                </td>
                              )}
                              <td className="py-2 font-medium text-slate-900 dark:text-slate-100">
                                {training.course?.title}
                              </td>
                              <td className="py-2">
                                <Badge color={isSU ? 'red' : 'blue'} className="text-xs">
                                  {getCourseTypeLabel(training.course?.type)}
                                </Badge>
                              </td>
                              <td className="py-2 text-slate-500 dark:text-slate-400">
                                {training.completedAt
                                  ? new Date(training.completedAt).toLocaleDateString('ro-RO')
                                  : '—'}
                              </td>
                              <td className="py-2">
                                <span
                                  className={`font-bold ${(training.score || 0) >= 80 ? 'text-accent-600 dark:text-accent-300' : 'text-danger-600 dark:text-danger-300'}`}
                                >
                                  {training.score != null ? `${training.score}%` : '—'}
                                </span>
                              </td>
                              <td className="py-2">
                                {renderSigIcons(training)}
                                {(() => {
                                  const next = getNextRequiredSlot(training);
                                  if (!next)
                                    return (
                                      <div className="text-xs text-accent-600 dark:text-accent-300 mt-1">
                                        {t('sheets.completeSignedCheck')}
                                      </div>
                                    );
                                  // Cross-training ordering: înainte să zicem „rândul tău", verificăm că prerequisite-le sunt OK.
                                  const xtBlockers = getCrossTrainingBlockersFE(
                                    training,
                                    empData.trainings,
                                    next
                                  );
                                  if (xtBlockers.length > 0) {
                                    return (
                                      <div
                                        className="text-xs text-warning-700 dark:text-warning-300 mt-1 font-medium"
                                        title={xtBlockers.join('; ')}
                                      >
                                        {t('sheets.blockedPrefix', {
                                          reason: xtBlockers.join('; '),
                                        })}
                                      </div>
                                    );
                                  }
                                  if (canFillSlot(next, training)) return null;
                                  return (
                                    <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                                      {t('sheets.waitingFor')}: {slotLabel(next)}
                                    </div>
                                  );
                                })()}
                              </td>
                              <td className="py-2 text-right">
                                <div className="flex gap-1 justify-end">
                                  {(() => {
                                    // Ascunde „Semnează" dacă există blocaje cross-training (chiar dacă e rândul logic al userului).
                                    const next = getNextRequiredSlot(training);
                                    const xtBlockers = next
                                      ? getCrossTrainingBlockersFE(
                                          training,
                                          empData.trainings,
                                          next
                                        )
                                      : [];
                                    const showSign = myPending && xtBlockers.length === 0;
                                    return showSign ? (
                                      <Button
                                        variant="accent"
                                        size="sm"
                                        onClick={() =>
                                          navigate(`/training/${training.id}/sign`, {
                                            state: { from: 'sheets' },
                                          })
                                        }
                                      >
                                        {t('sheets.signAction')}
                                      </Button>
                                    ) : null;
                                  })()}
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() =>
                                      navigate(`/training/${training.id}/sign`, {
                                        state: { from: 'sheets' },
                                      })
                                    }
                                  >
                                    {t('sheets.detailsAction')}
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>

                    {/* Admis la lucru — vizibil + acțiune pentru administrator */}
                    {!['COMPANY_ADMIN', 'SSM_COMPANY_ADMIN', 'SSM_INSTRUCTOR'].includes(
                      emp?.user?.role
                    ) &&
                      (() => {
                        const userIsAdminOfCompany =
                          user?.role === 'COMPANY_ADMIN' && emp?.companyId === user?.companyId;
                        const ilmSSM = empData.trainings.find((t) => t.course?.type === 'ILM_SSM');
                        const ilmSU = empData.trainings.find((t) => t.course?.type === 'ILM_SU');
                        const ilmSSMComplete = ilmSSM && !getNextRequiredSlot(ilmSSM);
                        const ilmSUComplete = ilmSU && !getNextRequiredSlot(ilmSU);
                        const handleAdmit = (domain) => {
                          // Pre-check: dacă lipsește o semnătură pe IIG sau ILM, arătăm popup
                          // explicativ în loc să deschidem modalul de parolă degeaba.
                          const blockers = computeAdmissionBlockers(empData, domain);
                          if (blockers.length > 0) {
                            setBlockedAdmissionInfo({
                              employeeName:
                                `${emp?.user?.firstName || ''} ${emp?.user?.lastName || ''}`.trim(),
                              domain,
                              blockers,
                            });
                            return;
                          }
                          setAdmitConfirm({ employee: emp, domain });
                        };
                        // Helpers pentru bulk mode — verifică dacă admis-la-lucru e ELIGIBIL (toate pre-requisite-ele OK).
                        const ssmAdmitEligible =
                          userIsAdminOfCompany &&
                          !emp?.admittedSSMBy &&
                          ilmSSMComplete &&
                          computeAdmissionBlockers(empData, 'SSM').length === 0;
                        const suAdmitEligible =
                          userIsAdminOfCompany &&
                          !emp?.admittedSUBy &&
                          ilmSUComplete &&
                          computeAdmissionBlockers(empData, 'SU').length === 0;
                        const ssmKey = `${emp.id}-SSM`;
                        const suKey = `${emp.id}-SU`;
                        return (
                          <div className="mt-3 pt-3 border-t border-slate-200 dark:border-slate-800 flex flex-wrap gap-3 items-center">
                            <span className="text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide">
                              {t('sheets.admitAtWorkLabel')}
                            </span>
                            <div className="flex items-center gap-2 flex-wrap">
                              {emp?.admittedSSMBy ? (
                                <Badge color="green">✓ SSM</Badge>
                              ) : bulkMode && ssmAdmitEligible ? (
                                <label className="inline-flex items-center gap-1.5 text-xs font-medium px-2 py-1 rounded bg-accent-50 dark:bg-accent-950/50 border border-accent-200 dark:border-accent-700 cursor-pointer hover:bg-accent-100 dark:hover:bg-accent-900">
                                  <input
                                    type="checkbox"
                                    className="h-3.5 w-3.5 text-accent-600 rounded"
                                    checked={selectedAdmissionItems.has(ssmKey)}
                                    onChange={() => toggleAdmissionSelected(emp.id, 'SSM')}
                                  />
                                  <span className="text-accent-700 dark:text-accent-300">
                                    {t('sheets.admitSSMLabel')}
                                  </span>
                                </label>
                              ) : userIsAdminOfCompany && ilmSSMComplete ? (
                                <Button
                                  size="sm"
                                  variant="accent"
                                  onClick={() => handleAdmit('SSM')}
                                >
                                  {t('sheets.admitWorkSSM')}
                                </Button>
                              ) : (
                                <Badge color="yellow">{t('sheets.ssmNotSignedBadge')}</Badge>
                              )}
                              {emp?.admittedSUBy ? (
                                <Badge color="green">✓ SU</Badge>
                              ) : bulkMode && suAdmitEligible ? (
                                <label className="inline-flex items-center gap-1.5 text-xs font-medium px-2 py-1 rounded bg-accent-50 dark:bg-accent-950/50 border border-accent-200 dark:border-accent-700 cursor-pointer hover:bg-accent-100 dark:hover:bg-accent-900">
                                  <input
                                    type="checkbox"
                                    className="h-3.5 w-3.5 text-accent-600 rounded"
                                    checked={selectedAdmissionItems.has(suKey)}
                                    onChange={() => toggleAdmissionSelected(emp.id, 'SU')}
                                  />
                                  <span className="text-accent-700 dark:text-accent-300">
                                    {t('sheets.admitSULabel')}
                                  </span>
                                </label>
                              ) : userIsAdminOfCompany && ilmSUComplete ? (
                                <Button
                                  size="sm"
                                  variant="accent"
                                  onClick={() => handleAdmit('SU')}
                                >
                                  {t('sheets.admitWorkSU')}
                                </Button>
                              ) : (
                                <Badge color="yellow">{t('sheets.suNotSignedBadge')}</Badge>
                              )}
                            </div>
                          </div>
                        );
                      })()}
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}
      <div className="text-xs text-slate-400 dark:text-slate-400 text-center">
        {t('sheets.legalFooter')}
      </div>

      <Modal
        open={!!admitConfirm}
        onClose={() => setAdmitConfirm(null)}
        title={t('sheets.admitModalTitlePrefix', { domain: admitConfirm?.domain || '' })}
        footer={
          <div className="flex gap-3 justify-end">
            <Button variant="secondary" onClick={() => setAdmitConfirm(null)}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="accent"
              onClick={() => {
                // Trecem la modal-ul de parolă (AdES); confirmarea propriu-zisă se face acolo.
                setPendingAdmit({
                  employeeId: admitConfirm.employee.id,
                  domain: admitConfirm.domain,
                });
                setAdmitConfirm(null);
              }}
            >
              {t('sheets.yesAdmitAtWork')}
            </Button>
          </div>
        }
      >
        {admitConfirm && (
          <div className="space-y-3 text-sm text-slate-700 dark:text-slate-300">
            <p>
              {t('sheets.confirmAdmitMsg', {
                domain: admitConfirm.domain,
                name: `${admitConfirm.employee?.user?.firstName || ''} ${admitConfirm.employee?.user?.lastName || ''}`.trim(),
              })}
            </p>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {t('sheets.admitLegalNote', { domain: admitConfirm.domain })}
            </p>
          </div>
        )}
      </Modal>

      {/* Bara de acțiune bulk — fixă jos când există selecție.
                `lg:left-64` lasă spațiu pentru sidebar-ul w-64 pe desktop, pe mobil (sidebar colapsat) left-0. */}
      {bulkMode && totalSelectedCount > 0 && (
        <div className="fixed bottom-0 left-0 right-0 lg:left-64 bg-primary-600 text-white shadow-2xl z-40">
          <div className="max-w-7xl mx-auto px-4 py-3 flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="font-semibold text-sm">
                {uniqueSelectedEmployees === 1
                  ? t('sheets.sheetsSelected', {
                      count: totalSelectedCount,
                      employeeCount: uniqueSelectedEmployees,
                    })
                  : t('sheets.sheetsSelectedPlural', {
                      count: totalSelectedCount,
                      employeeCount: uniqueSelectedEmployees,
                    })}
                {selectedTestTrainingIds.size > 0 && (
                  <span className="text-xs text-primary-100 ml-2">
                    {selectedTestTrainingIds.size === 1
                      ? t('sheets.sheetsPlusTestsOne', {
                          sheets: selectedTrainingIds.size,
                          tests: selectedTestTrainingIds.size,
                        })
                      : t('sheets.sheetsPlusTestsMany', {
                          sheets: selectedTrainingIds.size,
                          tests: selectedTestTrainingIds.size,
                        })}
                  </span>
                )}
              </p>
              <p className="text-xs text-primary-100">{t('sheets.savedSignatureFooter')}</p>
            </div>
            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={clearBulkSelection}
                className="text-white hover:bg-primary-700"
              >
                {t('sheets.deselectAll')}
              </Button>
              <Button
                variant="accent"
                size="md"
                onClick={() => setShowBulkConfirm(true)}
                loading={bulkLoading}
              >
                {t('sheets.signNSheets', { count: totalSelectedCount })}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal confirmare bulk */}
      <Modal
        open={showBulkConfirm}
        onClose={() => setShowBulkConfirm(false)}
        title={t('sheets.bulkConfirmTitle')}
        footer={
          <div className="flex gap-3 justify-end">
            <Button variant="secondary" onClick={() => setShowBulkConfirm(false)}>
              {t('common.cancel')}
            </Button>
            <Button variant="accent" onClick={handleBulkSignConfirm} loading={bulkLoading}>
              {t('sheets.yesBulkSign', { count: totalSelectedCount })}
            </Button>
          </div>
        }
      >
        <div className="space-y-3 text-sm text-slate-700 dark:text-slate-300">
          <p>
            {uniqueSelectedEmployees === 1
              ? t('sheets.bulkConfirmMsgOne', {
                  sheetCount: totalSelectedCount,
                  count: uniqueSelectedEmployees,
                })
              : t('sheets.bulkConfirmMsgMany', {
                  sheetCount: totalSelectedCount,
                  count: uniqueSelectedEmployees,
                })}
          </p>
          {/* Notă pentru admin care e ȘI CLM: pentru fișele ILM, semnătura va acoperi
                        AMBELE slot-uri (MANAGER + ADMIN). Backend-ul creează 2 rânduri de semnătură
                        per training, dar UI-ul „număr de semnături" arată o singură intrare.
                        Avertizăm explicit ca user-ul să nu fie surprins de încărcarea dublă. */}
          {user?.role === 'COMPANY_ADMIN' && (user?.isCLM || user?.managesDepartment) && (
            <div className="rounded-lg border border-primary-200 dark:border-primary-700 bg-primary-50 dark:bg-primary-950/30 p-3 text-xs text-primary-800 dark:text-primary-200 flex items-start gap-2">
              <svg
                className="w-4 h-4 mt-0.5 flex-shrink-0"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <div>
                <p className="font-semibold">{t('sheets.clmAndAdminTitle')}</p>
                <p className="mt-0.5">
                  <Trans i18nKey="sheets.clmAndAdminBody" components={{ b: <strong /> }} />
                </p>
              </div>
            </div>
          )}
          {/* Lista a tot ce se semnează. Inline când e scurtă; altfel un link care deschide
                        popup cu lista completă — user-ul trebuie să poată vedea exact ce semnează. */}
          {bulkFullList.length <= 10 ? (
            <ul className="text-xs text-slate-600 dark:text-slate-300 space-y-1 max-h-48 overflow-y-auto bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-800 rounded p-2">
              {bulkFullList.map((d) => (
                <li key={d.trainingId || d.key} className="flex items-start gap-2">
                  <span className="text-slate-400 dark:text-slate-400">
                    {d.kind === 'admission' ? '✓' : '•'}
                  </span>
                  <span>
                    {d.employeeName} — {d.courseTitle}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <button
              type="button"
              onClick={() => setShowBulkFullList(true)}
              className="text-xs font-medium text-primary-600 dark:text-primary-300 hover:underline inline-flex items-center gap-1"
            >
              {t('sheets.viewFullList', { count: bulkFullList.length })}
              <svg
                className="w-3.5 h-3.5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            </button>
          )}
          <p className="text-xs text-slate-500 dark:text-slate-400">
            {t('sheets.bulkIrreversibleNote')}
          </p>
        </div>
      </Modal>

      {/* Popup: lista completă a tot ce se semnează în bulk (grupat pe angajat). Deschis din
                link-ul „Vezi lista completă" din modalul de confirmare când lista e prea lungă. */}
      <Modal
        open={showBulkFullList}
        onClose={() => setShowBulkFullList(false)}
        title={t('sheets.fullListTitle', { count: bulkFullList.length })}
        footer={
          <div className="flex justify-end">
            <Button variant="primary" onClick={() => setShowBulkFullList(false)}>
              {t('common.close')}
            </Button>
          </div>
        }
      >
        <div className="space-y-3 max-h-[60vh] overflow-y-auto">
          {(() => {
            // Grupăm pe angajat ca lista să fie ușor de scanat.
            const byEmployee = {};
            bulkFullList.forEach((d) => {
              (byEmployee[d.employeeName] = byEmployee[d.employeeName] || []).push(d);
            });
            const kindBadge = (kind) => {
              if (kind === 'test')
                return {
                  label: t('sheets.kindTest'),
                  cls: 'bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300',
                };
              if (kind === 'jobDescription')
                return {
                  label: t('sheets.kindJobDescription'),
                  cls: 'bg-warning-100 text-warning-700 dark:bg-warning-900/40 dark:text-warning-300',
                };
              if (kind === 'admission')
                return {
                  label: t('sheets.kindAdmission'),
                  cls: 'bg-accent-100 text-accent-700 dark:bg-accent-900/40 dark:text-accent-300',
                };
              return {
                label: t('sheets.kindSheet'),
                cls: 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-200',
              };
            };
            return Object.entries(byEmployee).map(([name, items]) => (
              <div
                key={name}
                className="border border-slate-200 dark:border-slate-800 rounded-lg p-3 bg-slate-50 dark:bg-slate-800/50"
              >
                <p className="text-sm font-semibold text-slate-800 dark:text-slate-100 mb-2">
                  {name}
                </p>
                <ul className="space-y-1">
                  {items.map((d, i) => {
                    const b = kindBadge(d.kind);
                    return (
                      <li
                        key={(d.trainingId || d.key) + i}
                        className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300"
                      >
                        <span
                          className={`px-1.5 py-0.5 rounded text-[10px] font-medium uppercase tracking-wide ${b.cls}`}
                        >
                          {b.label}
                        </span>
                        <span>{d.courseTitle}</span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ));
          })()}
        </div>
      </Modal>

      {/* Modal rezultat bulk */}
      <Modal
        open={!!bulkResult}
        onClose={() => setBulkResult(null)}
        title={bulkResult?.error ? t('sheets.bulkErrorTitle') : t('sheets.bulkResultTitle')}
        footer={
          <div className="flex justify-end">
            <Button variant="primary" onClick={() => setBulkResult(null)}>
              {t('common.close')}
            </Button>
          </div>
        }
      >
        {bulkResult?.error ? (
          <p className="text-sm text-danger-700 dark:text-danger-300">{bulkResult.error}</p>
        ) : (
          bulkResult && (
            <div className="space-y-4">
              {(bulkResult.sheetCount > 0 ||
                bulkResult.testCount > 0 ||
                bulkResult.admittedCount > 0) && (
                <div className="p-4 bg-accent-50 dark:bg-accent-950/50 border border-accent-200 dark:border-accent-700 rounded-xl space-y-1.5">
                  <p className="font-bold text-accent-900 dark:text-accent-300 text-base">
                    {t('sheets.actionsPerformed')}
                  </p>
                  {bulkResult.sheetCount > 0 && (
                    <p className="text-accent-800 dark:text-accent-300 text-sm flex items-center gap-2">
                      <svg
                        className="w-4 h-4 text-accent-600 dark:text-accent-300"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2.5}
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                      {bulkResult.sheetCount === 1
                        ? t('sheets.sheetSignedOne', { count: bulkResult.sheetCount })
                        : t('sheets.sheetSignedMany', { count: bulkResult.sheetCount })}
                    </p>
                  )}
                  {bulkResult.testCount > 0 && (
                    <p className="text-accent-800 dark:text-accent-300 text-sm flex items-center gap-2">
                      <svg
                        className="w-4 h-4 text-accent-600 dark:text-accent-300"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2.5}
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                      {bulkResult.testCount === 1
                        ? t('sheets.testSignedOne', { count: bulkResult.testCount })
                        : t('sheets.testSignedMany', { count: bulkResult.testCount })}
                    </p>
                  )}
                  {bulkResult.admittedCount > 0 && (
                    <p className="text-accent-800 dark:text-accent-300 text-sm flex items-center gap-2">
                      <svg
                        className="w-4 h-4 text-accent-600 dark:text-accent-300"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2.5}
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                      {bulkResult.admittedCount === 1
                        ? t('sheets.admittedOne', { count: bulkResult.admittedCount })
                        : t('sheets.admittedMany', { count: bulkResult.admittedCount })}
                    </p>
                  )}
                  <p className="text-accent-700 dark:text-accent-300 text-xs italic mt-2">
                    {t('sheets.pdfsRegenerated')}
                  </p>
                </div>
              )}
              {bulkResult.skipped > 0 && bulkResult.details?.skipped?.length > 0 && (
                <div>
                  <p className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
                    {bulkResult.skipped === 1
                      ? t('sheets.skippedOne', { count: bulkResult.skipped })
                      : t('sheets.skippedMany', { count: bulkResult.skipped })}
                    :
                  </p>
                  <ul className="text-xs space-y-1 max-h-60 overflow-y-auto">
                    {bulkResult.details.skipped.map((s, i) => (
                      <li
                        key={i}
                        className="flex items-start gap-2 p-2 bg-warning-50 dark:bg-warning-950/50 border border-warning-200 dark:border-warning-700 rounded"
                      >
                        <svg
                          className="w-4 h-4 text-warning-600 dark:text-warning-300 flex-shrink-0 mt-0.5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                          />
                        </svg>
                        <div className="flex-1">
                          <div className="text-slate-800 dark:text-slate-100">
                            {s.employeeName} — {s.courseTitle}
                          </div>
                          <div className="text-slate-500 dark:text-slate-400">{s.message}</div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {bulkResult.pdfErrors > 0 && (
                <p className="text-xs text-warning-700 dark:text-warning-300 bg-warning-50 dark:bg-warning-950/50 border border-warning-200 dark:border-warning-700 rounded p-2">
                  {t('sheets.pdfRegenWarning', { count: bulkResult.pdfErrors })}
                </p>
              )}
              {bulkResult.admitSkipped && bulkResult.admitSkipped.length > 0 && (
                <div>
                  <p className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
                    {t('sheets.admitSkippedHeader', { count: bulkResult.admitSkipped.length })}
                  </p>
                  <ul className="text-xs space-y-1 max-h-48 overflow-y-auto">
                    {bulkResult.admitSkipped.map((a, i) => {
                      // Traducere user-friendly a reason-ului tehnic returnat de attemptAutoAdmit.
                      const reasonKey = `sheets.admitSkipReasons.${a.reason}`;
                      const reasonTranslated = t(reasonKey);
                      const reasonLabel =
                        reasonTranslated === reasonKey ? a.reason : reasonTranslated;
                      const emp = employeeMap?.[a.employeeId]?.employee;
                      const empName = emp
                        ? `${emp.user?.firstName || ''} ${emp.user?.lastName || ''}`.trim()
                        : a.employeeId;
                      return (
                        <li
                          key={i}
                          className="flex items-start gap-2 p-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded"
                        >
                          <span className="text-slate-400 dark:text-slate-500">•</span>
                          <div className="flex-1">
                            <div className="text-slate-800 dark:text-slate-100">
                              {empName} — {t('sheets.admitDomainLabel', { domain: a.domain })}
                            </div>
                            <div className="text-slate-500 dark:text-slate-400">{reasonLabel}</div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </div>
          )
        )}
      </Modal>

      {/* Modal preview test — afișează întrebări + răspunsuri date de angajat cu cod color.
                Aliniat cu rendering-ul din SignaturePage (Test rezolvat section). */}
      <Modal
        open={!!viewTest}
        onClose={() => setViewTest(null)}
        title={
          viewTest
            ? t('sheets.testViewTitle', { title: viewTest.courseTitle }) +
              (viewTest.employeeName ? ` · ${viewTest.employeeName}` : '')
            : t('sheets.testLabel')
        }
        size="2xl"
      >
        {viewTest?.loading && (
          <div className="py-8 text-center text-sm text-slate-500 dark:text-slate-400">
            {t('sheets.loadingTest')}
          </div>
        )}
        {viewTest &&
          !viewTest.loading &&
          viewTest.training &&
          (() => {
            const tr = viewTest.training;
            const questions = tr.course?.finalTest?.questions || [];
            const userAnswers = tr.testResults?.[0]?.answers || {};
            if (questions.length === 0) {
              return (
                <p className="py-4 text-sm text-slate-500 dark:text-slate-400">
                  {t('sheets.noFinalTest')}
                </p>
              );
            }
            const score = tr.score;
            const scorePassed =
              score != null && score >= (tr.course?.finalTest?.passingScore || 80);
            return (
              <div className="space-y-4">
                {score != null && (
                  <div
                    className={`flex items-center justify-between p-3 rounded-lg border ${scorePassed ? 'bg-accent-50 dark:bg-accent-950/30 border-accent-200 dark:border-accent-700' : 'bg-danger-50 dark:bg-danger-950/30 border-danger-200 dark:border-danger-700'}`}
                  >
                    <span className="text-sm font-medium text-slate-700 dark:text-slate-200">
                      {t('sheets.scoreObtained')}
                    </span>
                    <span
                      className={`text-lg font-bold ${scorePassed ? 'text-accent-700 dark:text-accent-300' : 'text-danger-700 dark:text-danger-300'}`}
                    >
                      {score}%
                    </span>
                  </div>
                )}
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  {t('sheets.testLegend')}
                </p>
                {questions.map((q, qi) => {
                  const selectedAnswerId = userAnswers[q.id];
                  return (
                    <div
                      key={q.id}
                      className="border border-slate-200 dark:border-slate-800 rounded-lg p-3"
                    >
                      <p className="font-medium text-sm text-slate-900 dark:text-slate-100 mb-2">
                        {qi + 1}. {q.text}
                      </p>
                      <div className="space-y-1">
                        {(q.answers || []).map((a) => {
                          const isSelected = a.id === selectedAnswerId;
                          const isCorrect = a.isCorrect;
                          let className = 'flex items-center gap-2 text-sm px-2 py-1 rounded ';
                          if (isSelected && isCorrect)
                            className +=
                              'bg-accent-50 dark:bg-accent-950/50 text-accent-700 dark:text-accent-300 font-medium';
                          else if (isSelected && !isCorrect)
                            className +=
                              'bg-danger-50 dark:bg-danger-950/50 text-danger-700 dark:text-danger-300 font-medium';
                          else if (isCorrect) className += 'text-accent-600 dark:text-accent-300';
                          else className += 'text-slate-500 dark:text-slate-400';
                          return (
                            <div key={a.id} className={className}>
                              {isSelected ? (
                                isCorrect ? (
                                  <svg
                                    className="w-4 h-4 text-accent-500 dark:text-accent-300 shrink-0"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                    strokeWidth={2}
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                    />
                                  </svg>
                                ) : (
                                  <svg
                                    className="w-4 h-4 text-danger-500 dark:text-danger-300 shrink-0"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                    strokeWidth={2}
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      d="M9.75 9.75l4.5 4.5m0-4.5l-4.5 4.5M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                    />
                                  </svg>
                                )
                              ) : (
                                <span className="w-4 h-4 rounded-full border border-slate-300 dark:border-slate-700 shrink-0" />
                              )}
                              <span>{a.text}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })()}
      </Modal>
    </div>
  );
};

export default EmployeeSheets;
